<div class="min-h-screen bg-gray-50">
  <div class="w-full px-4 sm:px-6 lg:px-8 py-6">
    <div class="text-sm text-gray-500" *ngIf="!details">Cargando…</div>

    <ng-container *ngIf="details">
      <!-- ============================================================ -->
      <!-- Header consolidado: breadcrumb + título + metadata           -->
      <!-- ============================================================ -->
      <div class="mb-6">
        <!-- Breadcrumb compacto -->
        <nav class="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-2"
             aria-label="Breadcrumb">
          <a [routerLink]="['/dashboard']" class="hover:text-slate-700 transition-colors">Dashboard</a>
          <span class="text-slate-300">/</span>
          <a [routerLink]="['/dashboard/enrollments']" class="hover:text-slate-700 transition-colors">Inscripciones</a>
          <span class="text-slate-300">/</span>
          <span class="hover:text-slate-700">Corporativas</span>
          <span class="text-slate-300">/</span>
          <span class="text-slate-700 tabular-nums">ACU-{{ details.agreementId || details.id }}</span>
          <span class="text-slate-300" *ngIf="details.id">·</span>
          <span class="text-slate-500 tabular-nums" *ngIf="details.id" title="ID corporativo en DB">#{{ details.id }}</span>
        </nav>

        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h1 class="text-2xl font-semibold text-gray-900 leading-tight flex items-baseline gap-2 flex-wrap">
              <span>ACU-{{ details.agreementId || details.id }}</span>
              <span class="text-gray-400 mx-1">·</span>
              <span class="text-gray-900">{{ details.company?.name || details.company?.businessName || details.companyName }}</span>
              <span *ngIf="details.id"
                    class="text-xs font-mono font-normal text-gray-400 ml-2"
                    title="ID en base de datos">
                #{{ details.id }}
              </span>
            </h1>
            <p class="text-sm text-gray-600 mt-1">
              <span *ngIf="details.courseName">{{ details.courseName }}</span>
              <ng-container *ngIf="details.courseName && (details.project?.name || details.projectName)">
                <span class="text-gray-400 mx-1.5">·</span>
              </ng-container>
              <span *ngIf="details.project?.name || details.projectName">Proyecto {{ details.project?.name || details.projectName }}</span>
            </p>
            <!-- Meta-strip: auditoría inline (sin caja redundante) -->
            <p class="text-xs text-gray-500 mt-2">
              <ng-container *ngIf="details.createdAt">
                Creado {{ details.createdAt | date:'dd/MM/yy HH:mm' }}
              </ng-container>
              <span *ngIf="details.createdBy"> por <strong>{{ details.createdBy }}</strong></span>
              <ng-container *ngIf="details.updatedAt">
                <span class="mx-1.5 text-gray-300">·</span>
                Modificado {{ details.updatedAt | date:'dd/MM/yy HH:mm' }}
                <span *ngIf="details.updatedBy"> por <strong>{{ details.updatedBy }}</strong></span>
              </ng-container>
            </p>
            <p class="text-xs text-blue-600 mt-1" *ngIf="isInternationalEnrollment">
              Internacional<ng-container *ngIf="details.exchangeRateCurrencyPair || details.currencyCode">
                · Tasa {{ details.exchangeRateCurrencyPair || (details.currencyCode + '/COP') }}: {{ details.exchangeRate | number:'1.0-2' }}
              </ng-container>
            </p>
          </div>

          <!-- Selector de estado (cuando hay permiso). Pill estática cuando no. -->
          <div class="shrink-0 flex items-center gap-2">
            <ng-container *ngIf="canChangeStatus; else statusPill">
              <label class="text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                Cambiar estado
              </label>
              <select
                class="border border-gray-300 rounded-md text-sm px-3 py-1.5 bg-white shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                [(ngModel)]="selectedStatus"
                (change)="onStatusChange(selectedStatus)"
                [style.min-width.px]="160"
              >
                <option *ngFor="let s of availableStatuses" [value]="s.value">{{ s.label }}</option>
              </select>
            </ng-container>
            <ng-template #statusPill>
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    [ngClass]="statusBadgeClasses(details.status)">
                <span class="w-1.5 h-1.5 rounded-full bg-current opacity-60"></span>
                {{ details.statusLabel || details.status }}
              </span>
            </ng-template>
          </div>
        </div>

        <!-- Acciones operativas del acuerdo -->
        <div class="mt-4 flex flex-wrap gap-2" *ngIf="!isEnrollmentCancelled">
          <button *ngIf="isEnrollmentActive && canWithdrawCorporateEnrollments"
                  (click)="goToWithdrawal()"
                  class="inline-flex items-center gap-2 text-sm bg-red-600 hover:bg-red-700 text-white font-medium px-3 py-2 rounded-md shadow-sm transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a7 7 0 00-7 7h14a7 7 0 00-7-7zM19 8v6m3-3h-6"/>
            </svg>
            Procesar Retiro
          </button>

          <button *ngIf="isEnrollmentActive && canDeferCorporateEnrollments"
                  (click)="goToDeferral()"
                  class="inline-flex items-center gap-2 text-sm bg-amber-600 hover:bg-amber-700 text-white font-medium px-3 py-2 rounded-md shadow-sm transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Procesar Aplazamiento
          </button>

          <button *ngIf="isEnrollmentWithdrawn && canReverseCorporateWithdrawals"
                  (click)="goToReversal()"
                  class="inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 py-2 rounded-md shadow-sm transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Revertir Retiro
          </button>

          <button (click)="goToAddParticipants()"
                  class="inline-flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3 py-2 rounded-md shadow-sm transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 4v16m8-8H4"/>
            </svg>
            Agregar Participantes
          </button>

          <button *ngIf="isSuperAdmin && !isEnrollmentCancelled && details.status !== 'Reversed' && details.status !== 'Withdrawn'"
                  (click)="openCancellationModal()"
                  class="inline-flex items-center gap-2 text-sm bg-white hover:bg-red-50 text-red-700 border border-red-200 font-medium px-3 py-2 rounded-md shadow-sm transition">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
            </svg>
            Anular Acuerdo
          </button>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- Banners de alerta (paleta unificada)                         -->
      <!-- ============================================================ -->
      <div class="mb-6 space-y-3">
        <!-- Crítico: acuerdo anulado -->
        <div *ngIf="isEnrollmentCancelled"
             class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
          <svg class="w-5 h-5 text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"/>
          </svg>
          <div class="text-sm text-red-800">
            <p class="font-medium">Acuerdo anulado</p>
            <p class="text-red-700 mt-0.5" *ngIf="details.cancellationReason">{{ details.cancellationReason }}</p>
          </div>
        </div>

        <!-- Atención: estado retirado/aplazado (rich) -->
        <div *ngIf="isWithdrawnOrPostponed && !isEnrollmentCancelled"
             class="rounded-lg border-l-4 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-400 px-4 py-3 flex items-start gap-3 shadow-sm">
          <div class="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap mb-1">
              <p class="text-base font-bold text-orange-900">
                Acuerdo Corporativo {{ details.statusLabel || details.status }}
              </p>
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-200 text-orange-800">
                <span class="w-1.5 h-1.5 rounded-full bg-orange-600"></span>
                {{ details.statusLabel || details.status }}
              </span>
            </div>
            <p class="text-sm text-orange-800">
              Esta inscripción tiene cartera castigada y no genera cobros activos.
            </p>
            <p class="text-xs text-orange-700 mt-1" *ngIf="latestPortfolioMovementReason">
              <strong>Razón:</strong> {{ latestPortfolioMovementReason }}
            </p>
          </div>
        </div>

        <!-- Atención: backend marcó la inscripción como needsAttention
             (TotalAmount de una versión del plan ≠ suma de cuotas activas) -->
        <div *ngIf="details?.needsAttention"
             class="rounded-lg border-2 border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
          <svg class="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <div class="text-sm text-yellow-900 flex-1">
            <p class="font-semibold">Esta inscripción corporativa necesita atención</p>
            <p class="text-yellow-800 mt-0.5">
              El total asignado en cuotas no coincide con el total registrado de una o más versiones del plan.
            </p>
            <p *ngIf="details?.needsAttentionReason" class="text-yellow-900 text-xs mt-2 font-mono">
              {{ details.needsAttentionReason }}
            </p>
          </div>
        </div>

        <!-- Info: inscripción internacional + 4-mini-cards -->
        <div *ngIf="isInternationalEnrollment"
             class="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
          <div class="flex items-start gap-3 mb-3">
            <svg class="w-5 h-5 text-purple-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-purple-900">Inscripción corporativa internacional</p>
              <p class="text-xs text-purple-800 mt-0.5">
                Montos en moneda extranjera. Pagos a Wompi se convierten a COP automáticamente.
              </p>
            </div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div class="bg-white border border-purple-200 rounded-md p-2">
              <p class="text-[10px] text-purple-600 uppercase tracking-wider">Moneda</p>
              <p class="font-bold text-purple-900">{{ getEnrollmentCurrencyCode() }}</p>
            </div>
            <div class="bg-white border border-purple-200 rounded-md p-2">
              <p class="text-[10px] text-purple-600 uppercase tracking-wider">TRM</p>
              <p class="font-bold text-purple-900 tabular-nums">{{ formatExchangeRate() }}</p>
            </div>
            <div class="bg-white border border-purple-200 rounded-md p-2">
              <p class="text-[10px] text-purple-600 uppercase tracking-wider">Valor Total</p>
              <p class="font-bold text-purple-900 tabular-nums">
                {{ formatCurrencyValue(getTotalEnrollmentValue(), getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}
              </p>
            </div>
            <div class="bg-white border border-purple-200 rounded-md p-2">
              <p class="text-[10px] text-purple-600 uppercase tracking-wider">Equivalente COP</p>
              <p class="font-bold text-purple-900 tabular-nums">{{ calculateCOPEquivalent(getTotalEnrollmentValue()) }}</p>
            </div>
          </div>
        </div>

        <!-- ⚠️ Cobro automático DESACTIVADO -->
        <div *ngIf="details && details.autoGeneratePaymentLinks === false && !isEnrollmentCancelled"
             class="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
          <div class="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-bold text-yellow-900">Recaudo automático desactivado</p>
            <p class="text-xs text-yellow-800 mt-0.5">
              Esta inscripción tiene desactivado el recaudo automático vía Wompi.
              No se generarán links de pago automáticos para las cuotas pendientes.
              Si necesitás reactivarlo, usá el toggle <strong>Cobro automático</strong> en la sección del plan de pagos.
            </p>
          </div>
        </div>

        <!-- 🆕 Divergencia: valor matrícula × participantes ≠ suma de cuotas activas -->
        <div *ngIf="corporateNeedsRedistribution.needs"
             class="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <svg class="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <div class="flex-1 min-w-0 text-sm text-amber-800">
            <p class="font-bold text-amber-900">El valor de la inscripción no coincide con la suma de cuotas</p>
            <p class="text-xs mt-0.5 tabular-nums">
              Valor esperado: {{ formatCurrencyValue(corporateNeedsRedistribution.expected, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}
              (valor por estudiante × {{ inscriptionContext?.activeParticipantsCount }} participante(s))
              · Suma de cuotas activas: {{ formatCurrencyValue(corporateNeedsRedistribution.sumActive, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}
              · Diferencia: {{ formatCurrencyValue(corporateNeedsRedistribution.delta, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}.
              Edita el plan de pagos para redistribuir los saldos.
            </p>
            <button type="button" (click)="goToEditCompanyPlans()"
                    [disabled]="isRecalculatingCompany"
                    class="mt-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-40">
              {{ isRecalculatingCompany ? 'Recalculando…' : 'Editar plan de pagos' }}
            </button>
          </div>
        </div>

        <!-- Atención: cartera castigada global -->
        <div *ngIf="hasGlobalPunishedPortfolio"
             class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <svg class="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 6h18M3 14h18M3 18h18"/>
          </svg>
          <div class="text-sm text-amber-800">
            <p class="font-medium">Cartera castigada global: {{ globalPunishedAmount | currency:'COP':'symbol':'1.0-2' }}</p>
          </div>
        </div>
      </div>

      <!-- ============================================================ -->
      <!-- Tabs top-level                                                -->
      <!-- ============================================================ -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
          <button
            (click)="setTab('empresa')"
            [class.border-blue-600]="activeTab === 'empresa'"
            [class.text-blue-600]="activeTab === 'empresa'"
            [class.border-transparent]="activeTab !== 'empresa'"
            [class.text-gray-500]="activeTab !== 'empresa'"
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm hover:text-gray-700"
          >
            Empresa
            <span class="ml-1.5 text-xs text-gray-400">({{ companyInstallmentsCount }})</span>
          </button>

          <button
            (click)="setTab('participantes')"
            [class.border-blue-600]="activeTab === 'participantes'"
            [class.text-blue-600]="activeTab === 'participantes'"
            [class.border-transparent]="activeTab !== 'participantes'"
            [class.text-gray-500]="activeTab !== 'participantes'"
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm hover:text-gray-700"
          >
            Participantes
            <span class="ml-1.5 text-xs text-gray-400">({{ participantsSummary.active }}/{{ participantsSummary.total }})</span>
          </button>

          <button
            (click)="setTab('ingresos')"
            [class.border-blue-600]="activeTab === 'ingresos'"
            [class.text-blue-600]="activeTab === 'ingresos'"
            [class.border-transparent]="activeTab !== 'ingresos'"
            [class.text-gray-500]="activeTab !== 'ingresos'"
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm hover:text-gray-700"
          >
            Ingresos Generales
            <span class="ml-1.5 text-xs text-gray-400">({{ generalIncomeReceipts.length || 0 }})</span>
          </button>

          <button
            (click)="setTab('auditoria')"
            [class.border-blue-600]="activeTab === 'auditoria'"
            [class.text-blue-600]="activeTab === 'auditoria'"
            [class.border-transparent]="activeTab !== 'auditoria'"
            [class.text-gray-500]="activeTab !== 'auditoria'"
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm hover:text-gray-700"
          >
            Auditoría
          </button>
        </nav>
      </div>

      <!-- ============================================================ -->
      <!-- Tab Empresa                                                   -->
      <!-- ============================================================ -->
      <section *ngIf="activeTab === 'empresa'">
        <!-- Último pago recibido (empresa) -->
        <div *ngIf="getLastPaymentDate(details?.companyPaymentPlans)"
             class="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
          <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-xs text-emerald-800">
            Último pago recibido:
            <strong>{{ getLastPaymentDate(details?.companyPaymentPlans) | date:'mediumDate' }}</strong>
          </span>
        </div>

        <!-- Resumen Empresa vs Participantes (side-by-side) -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <!-- Resumen Empresa -->
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
              </svg>
              Resumen Empresa
            </h3>
            <div class="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div class="bg-blue-50 border border-blue-200 rounded-md p-2">
                <p class="text-[10px] text-blue-600 uppercase tracking-wider">Total</p>
                <p class="font-bold text-blue-800 tabular-nums">{{ formatCurrencyValue(getCompanyExpectedTotal()) }}</p>
                <p *ngIf="getCompanyUnassignedAmount() > 0"
                   class="text-xs font-semibold text-yellow-700 mt-1">
                  Faltante por reasignar: {{ getCompanyUnassignedAmount() | currency:'COP':'symbol':'1.0-2' }}
                </p>
              </div>
              <div class="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                <p class="text-[10px] text-emerald-600 uppercase tracking-wider">Pagado</p>
                <p class="font-bold text-emerald-800 tabular-nums">{{ formatCurrencyValue(getCompanyPaidAmount()) }}</p>
              </div>
              <div class="bg-amber-50 border border-amber-200 rounded-md p-2">
                <p class="text-[10px] text-amber-700 uppercase tracking-wider">Pendiente</p>
                <p class="font-bold text-amber-800 tabular-nums">{{ formatCurrencyValue(getCompanyExpectedPending()) }}</p>
              </div>
              <div class="bg-red-50 border border-red-200 rounded-md p-2">
                <p class="text-[10px] text-red-700 uppercase tracking-wider">Vencido</p>
                <p class="font-bold text-red-800 tabular-nums">{{ formatCurrencyValue(getCompanyOverdueAmount()) }}</p>
              </div>
              <div class="bg-rose-50 border border-rose-200 rounded-md p-2">
                <p class="text-[10px] text-rose-700 uppercase tracking-wider">Castigada</p>
                <p class="font-bold text-rose-800 tabular-nums">{{ formatCurrencyValue(getCompanyCastigadoAmount()) }}</p>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-2">
              {{ getCompanyCompletedPaymentsCount() }} de {{ details?.companyPaymentPlans?.length || 0 }} cuotas pagadas
            </p>
          </div>

          <!-- Resumen Participantes -->
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              Resumen Participantes
            </h3>
            <div class="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div class="bg-blue-50 border border-blue-200 rounded-md p-2">
                <p class="text-[10px] text-blue-600 uppercase tracking-wider">Total</p>
                <p class="font-bold text-blue-800 tabular-nums">{{ formatCurrencyValue(getTotalParticipantsAmount()) }}</p>
              </div>
              <div class="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                <p class="text-[10px] text-emerald-600 uppercase tracking-wider">Pagado</p>
                <p class="font-bold text-emerald-800 tabular-nums">{{ formatCurrencyValue(getTotalParticipantsPaid()) }}</p>
              </div>
              <div class="bg-amber-50 border border-amber-200 rounded-md p-2">
                <p class="text-[10px] text-amber-700 uppercase tracking-wider">Pendiente</p>
                <p class="font-bold text-amber-800 tabular-nums">{{ formatCurrencyValue(getTotalParticipantsPending()) }}</p>
              </div>
              <div class="bg-red-50 border border-red-200 rounded-md p-2">
                <p class="text-[10px] text-red-700 uppercase tracking-wider">Vencido</p>
                <p class="font-bold text-red-800 tabular-nums">{{ formatCurrencyValue(getTotalParticipantsOverdue()) }}</p>
              </div>
              <div class="bg-rose-50 border border-rose-200 rounded-md p-2">
                <p class="text-[10px] text-rose-700 uppercase tracking-wider">Castigada</p>
                <p class="font-bold text-rose-800 tabular-nums">{{ formatCurrencyValue(getTotalParticipantsCastigado()) }}</p>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-2">
              {{ details?.participantEnrollments?.length || 0 }} participantes inscritos
            </p>
          </div>
        </div>

        <!-- % Completado global con progress bar -->
        <div class="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-semibold text-gray-900">% Completado del acuerdo</h3>
            <span class="text-lg font-bold text-indigo-700 tabular-nums">
              {{ getPaymentCompletionPercentage() | number:'1.1-1' }}%
            </span>
          </div>
          <div class="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div class="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
                 [style.width.%]="getPaymentCompletionPercentage() > 100 ? 100 : getPaymentCompletionPercentage()"></div>
          </div>
          <p class="text-xs text-gray-500 mt-1.5 tabular-nums">
            {{ formatCurrencyValue(getTotalPaidValue()) }} de {{ formatCurrencyValue(getTotalEnrollmentValue()) }}
          </p>
        </div>
        <!-- Grid 2-cols (1/4 contexto + 3/4 plan en desktop ancho) -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <!-- Columna izquierda: contexto -->
          <aside class="lg:col-span-1 space-y-4 lg:sticky lg:top-4 self-start">
            <!-- Card Empresa -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 class="text-sm font-semibold text-gray-900">Empresa</h3>
                <a *ngIf="details.company?.id"
                   [routerLink]="['/dashboard/companies/view-company', details.company.id]" target="_blank"
                   class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Ver &rarr;</a>
              </div>
              <div class="p-3">
                <div class="flex items-center gap-2 mb-3">
                  <div class="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm shrink-0"
                       [title]="details.company?.name || details.company?.businessName">
                    {{ (details.company?.name || details.company?.businessName || '?').charAt(0) }}{{ (details.company?.name || details.company?.businessName || '').split(' ')[1]?.charAt(0) || '' }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-bold text-sm text-gray-900 truncate" [title]="details.company?.name || details.company?.businessName">
                      {{ details.company?.name || details.company?.businessName || '—' }}
                    </p>
                    <p class="text-xs text-gray-500 truncate" *ngIf="details.company?.documentId || details.company?.nit || details.company?.identification">
                      NIT {{ details.company?.documentId || details.company?.nit || details.company?.identification }}
                    </p>
                  </div>
                </div>
                <div class="space-y-2 text-xs">
                  <div class="flex items-start gap-2" *ngIf="details.company?.address">
                    <svg class="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span class="text-gray-600 truncate">{{ details.company.address }}</span>
                  </div>
                  <div class="flex items-start gap-2" *ngIf="details.company?.email">
                    <svg class="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <a [href]="'mailto:' + details.company.email"
                       class="text-indigo-600 hover:underline truncate">
                      {{ details.company.email }}
                    </a>
                  </div>
                  <div class="flex items-start gap-2" *ngIf="details.company?.phone">
                    <svg class="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span class="text-gray-700">{{ details.company.phone }}</span>
                  </div>
                  <div *ngIf="!details.company?.address && !details.company?.email && !details.company?.phone"
                       class="text-gray-400 italic">
                    Sin datos de contacto registrados.
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Proyecto -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                 *ngIf="details.project || details.projectName">
              <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 class="text-sm font-semibold text-gray-900">Proyecto</h3>
                <a *ngIf="details.project?.id"
                   [routerLink]="['/dashboard/projects/view-project', details.project.id]" target="_blank"
                   class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Ver &rarr;</a>
              </div>
              <div class="p-3">
                <h4 class="font-bold text-sm text-gray-900 mb-0.5 truncate"
                    [title]="details.project?.name || details.projectName">
                  {{ details.project?.name || details.projectName }}
                </h4>
                <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3"
                   *ngIf="details.project?.projectId">
                  {{ details.project.projectId }}
                </p>
                <div class="space-y-1.5 text-xs">
                  <div class="flex justify-between py-1 border-b border-gray-100"
                       *ngIf="details.project?.currency?.code || details.currencyCode">
                    <span class="text-gray-500">Moneda</span>
                    <span class="font-medium text-gray-800">{{ details.project?.currency?.code || details.currencyCode || 'COP' }}</span>
                  </div>
                  <div class="flex justify-between py-1 border-b border-gray-100"
                       *ngIf="details.project?.period">
                    <span class="text-gray-500">Período</span>
                    <span class="font-medium text-gray-800 truncate ml-2">{{ details.project.period }}</span>
                  </div>
                  <div class="flex justify-between py-1 border-b border-gray-100"
                       *ngIf="details.project?.duration">
                    <span class="text-gray-500">Duración</span>
                    <span class="font-medium text-gray-800">{{ details.project.duration }} Sem</span>
                  </div>
                  <div class="flex justify-between py-1" *ngIf="details.projectStartDate">
                    <span class="text-gray-500">Inicio</span>
                    <span class="font-medium text-gray-800">{{ details.projectStartDate | date:'yyyy-MM-dd' }}</span>
                  </div>
                  <div class="flex justify-between py-1" *ngIf="details.projectEndDate">
                    <span class="text-gray-500">Fin</span>
                    <span class="font-medium text-gray-800">{{ details.projectEndDate | date:'yyyy-MM-dd' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Observaciones acuerdo (append-only) -->
            <div class="bg-white rounded-xl border border-gray-200 p-4">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-sm font-semibold text-gray-900">Observaciones del acuerdo</h3>
                <button *ngIf="canEditObservations && !isAddingObservation"
                        (click)="startAddingObservation()"
                        class="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  Agregar
                </button>
              </div>
              <div *ngIf="!isAddingObservation">
                <div *ngIf="details.observations"
                     class="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-2 max-h-48 overflow-y-auto">{{ details.observations }}</div>
                <p *ngIf="!details.observations" class="text-sm text-gray-400 italic">Sin observaciones</p>
              </div>
              <ng-container *ngIf="isAddingObservation">
                <p class="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Nueva observación</p>
                <textarea [(ngModel)]="newObservationDraft" rows="3"
                          maxlength="2000"
                          placeholder="Escribí una nueva observación. Se agregará al historial con fecha + usuario."
                          class="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"></textarea>
                <div class="flex justify-between items-center mt-2">
                  <span class="text-[10px] text-gray-500 tabular-nums">{{ newObservationDraft.length }}/2000</span>
                  <div class="flex gap-2">
                    <button (click)="cancelAddingObservation()"
                            [disabled]="isSavingObservation"
                            class="text-xs text-gray-600 hover:text-gray-800 px-2 py-1">Cancelar</button>
                    <button (click)="saveNewObservation()"
                            [disabled]="isSavingObservation || newObservationDraft.trim().length < 3"
                            class="text-xs px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                      {{ isSavingObservation ? 'Guardando...' : 'Agregar al historial' }}
                    </button>
                  </div>
                </div>
              </ng-container>
            </div>

            <!-- Timeline rich de movimientos de cartera corporativa -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 class="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Movimientos de cartera
                </h3>
                <span *ngIf="hasMovementHistory()"
                      class="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                  {{ movementHistory.length }}
                </span>
              </div>
              <div class="p-3">
                <p *ngIf="!hasMovementHistory()" class="text-sm text-gray-400 italic">
                  Sin movimientos registrados.
                </p>
                <ol *ngIf="hasMovementHistory()" class="relative border-l border-gray-200 ml-2 space-y-4">
                  <li *ngFor="let m of movementHistory" class="ml-4 relative">
                    <span class="absolute -left-[22px] mt-1 w-3 h-3 rounded-full border-2 border-white"
                          [ngClass]="getTimelineDotClass(m.movementType)"></span>
                    <div class="flex items-start justify-between gap-2 flex-wrap">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2 flex-wrap mb-0.5">
                          <p class="text-xs font-semibold text-gray-900">
                            {{ m.movementTypeDescription || getMovementTypeLabel(m.movementType) }}
                          </p>
                          <span *ngIf="m.isReverted"
                                class="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
                            Revertido
                          </span>
                        </div>
                        <p class="text-[11px] text-gray-500 tabular-nums">{{ m.movementDate | date:'dd/MM/yy HH:mm' }}</p>
                        <p class="text-xs text-gray-600 mt-0.5" *ngIf="m.totalAffectedAmount">
                          <strong class="tabular-nums">{{ formatCurrencyValue(m.totalAffectedAmount) }}</strong>
                          <span *ngIf="m.affectedInstallmentsCount" class="text-gray-500">
                            · {{ m.affectedInstallmentsCount }} cuota{{ m.affectedInstallmentsCount !== 1 ? 's' : '' }}
                          </span>
                        </p>
                        <p class="text-xs text-gray-700 mt-0.5" *ngIf="m.reason">{{ m.reason }}</p>
                        <p class="text-xs text-gray-500 mt-0.5 italic" *ngIf="m.notes">{{ m.notes }}</p>
                        <p class="text-[10px] text-gray-400 mt-0.5" *ngIf="m.performedBy">por {{ m.performedBy }}</p>
                      </div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </aside>

          <!-- Columna derecha: plan empresa -->
          <div class="lg:col-span-3 space-y-4">
            <section class="bg-white rounded-xl border overflow-hidden"
                     [ngClass]="isEnrollmentCancelled ? 'border-red-200' : 'border-gray-200'">
              <!-- Banner de Anulación (cabecera plan empresa) -->
              <div *ngIf="isEnrollmentCancelled"
                   class="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center gap-3">
                <div class="bg-red-100 w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-red-800">Plan de Pagos Anulado</p>
                  <p class="text-xs text-red-600">
                    Este acuerdo ha sido anulado. El plan de pagos es de solo lectura.
                  </p>
                  <p *ngIf="details.cancellationReason" class="text-xs text-red-700 mt-0.5">
                    <strong>Motivo:</strong> {{ details.cancellationReason }}
                  </p>
                </div>
                <span class="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium shrink-0">
                  <span class="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
                  Anulado
                </span>
              </div>

              <!-- Toolbar -->
              <div class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div class="min-w-0">
                  <h2 class="text-base font-semibold text-gray-900">Plan de pagos empresa</h2>
                  <div class="mt-1" *ngIf="details?.companyPaymentPlans?.length">
                    <app-installment-stats [plans]="details.companyPaymentPlans" variant="inline"
                      title="Estado de cuotas"></app-installment-stats>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0" *ngIf="!isEnrollmentCancelled">
                  <span class="text-xs font-medium text-gray-700">Cobro automático</span>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" [checked]="details?.autoGeneratePaymentLinks"
                           (change)="toggleAutoPayment($event)" class="sr-only peer">
                    <div class="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <!-- Acciones agrupadas: positivas (izq) vs destructivas (der) -->
              <div *ngIf="!isEnrollmentCancelled"
                   class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <div class="flex flex-wrap items-center gap-2">
                  <button (click)="goToEditCompanyPlans()"
                          [disabled]="isEditCompanyPlanDisabled() || isRecalculatingCompany"
                          [title]="isEditCompanyPlanDisabled() ? 'Sin saldo pendiente — nada que editar' : 'Recalcula los pagos y abre el editor del plan'"
                          class="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition"
                          [ngClass]="(isEditCompanyPlanDisabled() || isRecalculatingCompany)
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'">
                    {{ isRecalculatingCompany ? 'Recalculando…' : 'Editar plan' }}
                  </button>
                  <!-- 🆕 Papelera de cuotas eliminadas (permiso ENROLLMENTS_PAYMENT_PLAN_TRASH) -->
                  <button *ngIf="canSeeTrash" type="button" (click)="openTrashModal()"
                          title="Ver cuotas eliminadas y restaurarlas"
                          class="inline-flex items-center gap-1.5 text-xs text-gray-700 border border-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-50">
                    Papelera ({{ trashModal.count }})
                  </button>
                  <!-- 🆕 Editar valor de matrícula por estudiante (permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS) -->
                  <button *ngIf="isSuperAdmin" type="button" (click)="openEditAmountModal()"
                          title="Editar el valor de matrícula por estudiante y recalcular el descuento"
                          class="inline-flex items-center gap-1.5 text-xs text-sky-700 border border-sky-200 px-2.5 py-1.5 rounded-md hover:bg-sky-50">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                    </svg>
                    Valor matrícula
                  </button>
                  <a [routerLink]="['/dashboard/enrollments/payment-plans/history', agreementId]"
                     [queryParams]="{ type: 'corporate' }" target="_blank"
                     class="inline-flex items-center gap-1.5 text-xs text-gray-700 border border-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-50">
                    Historial
                  </a>
                  <button type="button" (click)="goToReceiptsCompany()"
                          class="inline-flex items-center gap-1.5 text-xs text-purple-700 border border-purple-200 px-2.5 py-1.5 rounded-md hover:bg-purple-50">
                    Ver comprobantes ↗
                  </button>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <button (click)="toggleCompanyPunishMode()"
                          [disabled]="!isCompanyPunishMode && areAllCompanyPaymentPlansPaid()"
                          class="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition"
                          [ngClass]="{
                            'bg-red-100 ring-2 ring-red-400 border-red-200 text-red-700 hover:bg-red-50': isCompanyPunishMode,
                            'border-red-200 text-red-700 hover:bg-red-50': !isCompanyPunishMode && !areAllCompanyPaymentPlansPaid(),
                            'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed': !isCompanyPunishMode && areAllCompanyPaymentPlansPaid()
                          }">
                    {{ isCompanyPunishMode ? 'Cancelar gestión' : 'Gestionar castigo' }}
                  </button>
                  <button *ngIf="isSuperAdmin && !isEnrollmentCancelled" (click)="openCancellationModal()"
                          class="inline-flex items-center gap-1.5 text-xs text-red-700 border border-red-200 px-2.5 py-1.5 rounded-md hover:bg-red-50">
                    Anular acuerdo
                  </button>
                </div>
              </div>

              <!-- Resumen rápido (5 mini-KPIs financieros con conversión COP si internacional) -->
              <div *ngIf="details?.companyPaymentPlans?.length"
                   class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 px-4 py-3 border-b border-gray-200">
                <div class="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p class="text-xs font-medium text-blue-600 mb-1">Total</p>
                  <p class="text-sm font-bold text-blue-800">{{ formatCurrencyValue(getCompanyExpectedTotal()) }}</p>
                </div>
                <div class="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p class="text-xs font-medium text-green-600 mb-1">Pagado</p>
                  <p class="text-sm font-bold text-green-800">{{ formatCurrencyValue(getCompanyPaidAmount()) }}</p>
                </div>
                <div class="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p class="text-xs font-medium text-orange-600 mb-1">Pendiente</p>
                  <p class="text-sm font-bold text-orange-800">{{ formatCurrencyValue(getCompanyExpectedPending()) }}</p>
                </div>
                <div class="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p class="text-xs font-medium text-red-600 mb-1">Vencido</p>
                  <p class="text-sm font-bold text-red-800">{{ formatCurrencyValue(getCompanyOverdueAmount()) }}</p>
                </div>
                <div class="bg-rose-50 p-3 rounded-lg border border-rose-200">
                  <p class="text-xs font-medium text-rose-700 mb-1">Castigada</p>
                  <p class="text-sm font-bold text-rose-800">{{ formatCurrencyValue(getCompanyCastigadoAmount()) }}</p>
                </div>
              </div>

              <!-- Panel de gestión de castigo (Empresa) -->
              <div *ngIf="isCompanyPunishMode" class="px-4 py-3 bg-red-50 border-b border-red-200">
                <div class="flex items-start gap-2 mb-3">
                  <svg class="w-4 h-4 text-red-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <div>
                    <p class="text-sm font-bold text-red-900">Modo de Gestión de Castigo (Empresa)</p>
                    <p class="text-xs text-red-700">Seleccioná las cuotas a castigar o descastigar. La razón es obligatoria.</p>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label class="block text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">
                      Razón del cambio *
                    </label>
                    <input [(ngModel)]="punishReason" type="text"
                           class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                           placeholder="Obligatoria">
                  </div>
                  <div>
                    <label class="block text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">
                      Notas adicionales
                    </label>
                    <input [(ngModel)]="punishNotes" type="text"
                           class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                           placeholder="Opcional">
                  </div>
                </div>
                <div class="flex items-center justify-between flex-wrap gap-2">
                  <span class="text-xs text-gray-700">
                    <span class="font-bold text-red-600">{{ selectedCompanyPlanIds.length }}</span> cuotas seleccionadas
                  </span>
                  <div class="flex gap-2">
                    <button (click)="executeCompanyPunishAction('punish')"
                            [disabled]="selectedCompanyPlanIds.length === 0 || !punishReason"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-xs font-medium">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      Castigar
                    </button>
                    <button (click)="executeCompanyPunishAction('unpunish')"
                            [disabled]="selectedCompanyPlanIds.length === 0 || !punishReason"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 text-xs font-medium">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      Descastigar
                    </button>
                  </div>
                </div>
              </div>

              <!-- Tabla de cuotas empresa -->
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-xs text-gray-500 uppercase">
                      <th class="px-2 py-2" *ngIf="isCompanyPunishMode"></th>
                      <th class="px-2 py-2 w-8" *ngIf="isSuperAdmin"
                          title="Arrastrá para reordenar las cuotas"></th>
                      <th class="px-2 py-2 w-8"></th>
                      <th class="px-2 py-2 text-left">Cuota</th>
                      <th class="px-2 py-2 text-left" title="PaymentPlan.Id en DB">ID</th>
                      <th class="px-3 py-2 text-left">Fecha</th>
                      <th class="px-3 py-2 text-right">Monto</th>
                      <th class="px-3 py-2 text-right">Pagado</th>
                      <th class="px-3 py-2 text-right" title="Monto pendiente = Monto - Pagado">Saldo Restante</th>
                      <th class="px-3 py-2 text-left">Estado</th>
                      <th class="px-3 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100"
                         cdkDropList
                         [cdkDropListDisabled]="!isSuperAdmin || isCompanyPunishMode"
                         (cdkDropListDropped)="onCompanyPlanDrop($event)">
                    <ng-container *ngFor="let cuota of details?.companyPaymentPlans">
                      <tr cdkDrag
                          [cdkDragData]="cuota"
                          [cdkDragDisabled]="!isSuperAdmin || isCompanyPunishMode"
                          cdkDragLockAxis="y"
                          class="hover:bg-gray-50"
                          [class.bg-red-50]="cuota?.isPunished">
                        <td class="px-2 py-2" *ngIf="isCompanyPunishMode">
                          <input type="checkbox"
                                 [checked]="selectedCompanyPlanIds.includes(cuota.id)"
                                 (change)="toggleCompanyPlanSelection(cuota.id)">
                        </td>
                        <td *ngIf="isSuperAdmin"
                            cdkDragHandle
                            class="px-2 py-2 text-center text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                            title="Arrastrar para reordenar">
                          <svg class="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z"/>
                          </svg>
                        </td>
                        <td class="px-2 py-2 text-center">
                          <button (click)="toggleCollapse(cuota)"
                                  class="text-gray-400 hover:text-indigo-600 transition-colors"
                                  [title]="cuota.isCollapsed === false ? 'Colapsar' : 'Ver comprobantes'">
                            <svg class="w-4 h-4 transition-transform" [ngClass]="{'rotate-180': cuota.isCollapsed === false}"
                                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </td>
                        <td class="px-2 py-2 text-gray-700 font-medium">{{ cuota.installmentNumber }}</td>
                        <td class="px-2 py-2 font-mono text-[11px] text-gray-500" title="PaymentPlan.Id">#{{ cuota.id }}</td>
                        <td class="px-3 py-2 text-gray-900">
                          {{ cuota.dueDate | date:'yyyy-MM-dd' }}
                          <p *ngIf="isPaymentOverdue(cuota) && !cuota.isPunished" class="text-[10px] text-red-500">
                            Vencida hace {{ getDaysOverdue(cuota) }} días
                          </p>
                        </td>
                        <td class="px-3 py-2 text-right text-gray-900">
                          {{ cuota.amountToPay | currency:'COP':'symbol':'1.0-2' }}
                          <span *ngIf="isInternationalEnrollment && !cuota.isPunished"
                                class="block text-[10px] text-purple-600">
                            ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountToPay) }}
                          </span>
                        </td>
                        <td class="px-3 py-2 text-right text-emerald-700">
                          {{ cuota.amountPaid | currency:'COP':'symbol':'1.0-2' }}
                          <span *ngIf="isInternationalEnrollment && cuota.amountPaid > 0 && !cuota.isPunished"
                                class="block text-[10px] text-purple-600">
                            ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountPaid) }}
                          </span>
                        </td>
                        <td class="px-3 py-2 text-right tabular-nums" [ngClass]="{
                          'text-gray-400 italic': cuota.isPunished,
                          'text-emerald-600 font-medium': !cuota.isPunished && (cuota.amountToPay - cuota.amountPaid) <= 0,
                          'text-rose-600 font-medium': !cuota.isPunished && isPaymentOverdue(cuota) && (cuota.amountToPay - cuota.amountPaid) > 0,
                          'text-gray-700': !cuota.isPunished && !isPaymentOverdue(cuota) && (cuota.amountToPay - cuota.amountPaid) > 0
                        }">
                          {{ (cuota.amountToPay - cuota.amountPaid) | currency:'COP':'symbol':'1.0-2' }}
                          <span *ngIf="isInternationalEnrollment && !cuota.isPunished && (cuota.amountToPay - cuota.amountPaid) > 0"
                                class="block text-[10px] text-purple-600">
                            ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountToPay - cuota.amountPaid) }}
                          </span>
                        </td>
                        <td class="px-3 py-2">
                          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                [ngClass]="getPaymentStatusClass(cuota.paymentStatus)">
                            {{ getPaymentStatusLabel(cuota.paymentStatus) }}
                          </span>
                          <span *ngIf="cuota.isPunished"
                                class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700">
                            Castigada
                          </span>
                        </td>
                        <td class="px-3 py-2 text-right">
                          <div class="inline-flex items-center gap-1">
                            <a *ngIf="cuota.paymentStatus !== 'PAID' && !cuota.isPunished && !isEnrollmentCancelled"
                               [routerLink]="['/dashboard/income-receipts/new']"
                               [queryParams]="{companyId: details.company?.id, projectId: details.project?.id, corporateEnrollmentId: details.id, installmentNumber: cuota.installmentNumber, isCompanyPayment: true}"
                               class="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md font-bold transition"
                               title="Registrar pago de esta cuota">
                              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                              </svg>
                              Pagar
                            </a>
                            <button *ngIf="isSuperAdmin"
                                    (click)="openAdminMenu(cuota)"
                                    class="text-xs text-purple-700 border border-purple-200 px-2 py-1 rounded-md hover:bg-purple-50">
                              Admin
                            </button>
                          </div>
                        </td>
                      </tr>

                      <!-- Fila colapsable: comprobantes activos / anulados / devoluciones -->
                      <tr *ngIf="cuota.isCollapsed === false" class="bg-gray-50 border-t border-gray-100">
                        <td [attr.colspan]="(isCompanyPunishMode ? 10 : 9) + (isSuperAdmin ? 1 : 0)" class="px-6 py-4">
                          <!-- Castigada info card -->
                          <div *ngIf="cuota.isPunished"
                               class="bg-rose-50 border border-rose-200 rounded-md p-3 mb-3">
                            <div class="flex items-start gap-2">
                              <svg class="w-4 h-4 text-rose-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                              </svg>
                              <div class="flex-1 min-w-0">
                                <p class="text-sm font-semibold text-rose-900 mb-1">Cartera Castigada</p>
                                <div class="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <p class="text-[10px] text-rose-600 uppercase tracking-wider">Monto castigado</p>
                                    <p class="font-bold text-rose-800 tabular-nums">
                                      {{ formatCurrencyValue(cuota.amountToPay - cuota.amountPaid) }}
                                    </p>
                                  </div>
                                  <div *ngIf="cuota.punishedAt">
                                    <p class="text-[10px] text-rose-600 uppercase tracking-wider">Fecha</p>
                                    <p class="text-rose-800">{{ cuota.punishedAt | date:'mediumDate' }}</p>
                                  </div>
                                  <div *ngIf="cuota.punishedBy">
                                    <p class="text-[10px] text-rose-600 uppercase tracking-wider">Por</p>
                                    <p class="text-rose-800">{{ cuota.punishedBy }}</p>
                                  </div>
                                </div>
                                <p *ngIf="cuota.punishReason" class="text-xs text-rose-800 mt-1.5">
                                  <strong>Razón:</strong> {{ cuota.punishReason }}
                                </p>
                                <p *ngIf="cuota.punishNotes" class="text-xs text-rose-700 mt-0.5 italic">
                                  {{ cuota.punishNotes }}
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- Observaciones de la cuota -->
                          <div *ngIf="cuota.observations"
                               class="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                            <p class="text-[10px] text-blue-700 uppercase tracking-wider mb-1">Observaciones</p>
                            <p class="text-sm text-blue-900 whitespace-pre-wrap">{{ cuota.observations }}</p>
                          </div>

                          <!-- Tabs nav -->
                          <div class="border-b border-gray-200 mb-3">
                            <nav class="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                              <button *ngIf="getActiveReceipts(cuota).length > 0"
                                      (click)="setCompanyPlanTab(cuota.id, 'active')"
                                      [class]="getCompanyPlanTab(cuota.id) === 'active'
                                        ? 'border-emerald-500 text-emerald-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                      class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5 transition-colors">
                                Comprobantes
                                <span class="bg-emerald-100 text-emerald-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                  {{ getActiveReceipts(cuota).length }}
                                </span>
                              </button>
                              <button *ngIf="getCancelledReceipts(cuota).length > 0"
                                      (click)="setCompanyPlanTab(cuota.id, 'cancelled')"
                                      [class]="getCompanyPlanTab(cuota.id) === 'cancelled'
                                        ? 'border-red-500 text-red-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                      class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5 transition-colors">
                                Anulados
                                <span class="bg-red-100 text-red-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                  {{ getCancelledReceipts(cuota).length }}
                                </span>
                              </button>
                              <button *ngIf="hasRefundsForCompanyPlan(cuota)"
                                      (click)="setCompanyPlanTab(cuota.id, 'refunds')"
                                      [class]="getCompanyPlanTab(cuota.id) === 'refunds'
                                        ? 'border-purple-500 text-purple-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                      class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5 transition-colors">
                                Devoluciones
                              </button>
                              <button (click)="setCompanyPlanTab(cuota.id, 'emails'); loadEmailsForPlan(cuota)"
                                      [class]="getCompanyPlanTab(cuota.id) === 'emails'
                                        ? 'border-sky-500 text-sky-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                      class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5 transition-colors">
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                </svg>
                                Emails
                                <span *ngIf="hasEmails(cuota)" class="bg-sky-100 text-sky-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                  {{ getEmailsForPlan(cuota).length }}
                                </span>
                              </button>
                              <span *ngIf="!getActiveReceipts(cuota).length && !getCancelledReceipts(cuota).length && !hasRefundsForCompanyPlan(cuota) && !hasEmails(cuota)"
                                    class="text-xs text-gray-500 py-2 ml-2">
                                Sin comprobantes ni movimientos registrados para esta cuota.
                              </span>
                            </nav>
                          </div>

                          <!-- Tab Emails -->
                          <div *ngIf="getCompanyPlanTab(cuota.id) === 'emails'" class="space-y-2">
                            <div *ngIf="isEmailsLoading(cuota)" class="text-center py-4">
                              <div class="inline-flex items-center gap-2 text-xs text-gray-500">
                                <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                                Cargando emails...
                              </div>
                            </div>
                            <div *ngIf="!isEmailsLoading(cuota) && !hasEmails(cuota)" class="text-center py-4 text-xs text-gray-500">
                              Sin emails registrados para esta cuota.
                            </div>
                            <div *ngFor="let email of getEmailsForPlan(cuota)"
                                 class="bg-white border border-gray-200 rounded-md p-3">
                              <div class="flex items-start justify-between gap-3 mb-1.5">
                                <div class="flex items-center gap-2 flex-wrap min-w-0">
                                  <span class="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                                        [ngClass]="emailLogService.getEmailTypeClass(email.emailType)">
                                    {{ emailLogService.getEmailTypeLabel(email.emailType) }}
                                  </span>
                                  <span class="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                                        [ngClass]="emailLogService.getSuccessClass(email.isSuccess)">
                                    {{ email.isSuccess ? 'Entregado' : 'Falló' }}
                                  </span>
                                </div>
                                <span class="text-[10px] text-gray-500 shrink-0 tabular-nums">{{ email.sentAt | date:'dd/MM/yy HH:mm' }}</span>
                              </div>
                              <p class="text-xs font-medium text-gray-900 truncate">{{ email.subject }}</p>
                              <p class="text-xs text-gray-500 truncate">
                                Para: {{ email.recipientName || email.recipientEmail }}
                                <span *ngIf="email.recipientName" class="text-gray-400"> &lt;{{ email.recipientEmail }}&gt;</span>
                              </p>
                              <p *ngIf="!email.isSuccess && email.errorMessage"
                                 class="mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                                Error: {{ email.errorMessage }}
                              </p>
                            </div>
                          </div>

                          <!-- Tab Comprobantes Activos -->
                          <div *ngIf="getCompanyPlanTab(cuota.id) === 'active' && getActiveReceipts(cuota).length > 0"
                               class="space-y-2">
                            <div *ngFor="let r of getActiveReceipts(cuota)"
                                 class="bg-white border border-gray-200 rounded-md p-3 space-y-3">
                              <!-- Header: concept + badges + acciones -->
                              <div class="flex items-start justify-between gap-3 flex-wrap">
                                <div class="min-w-0">
                                  <div class="flex items-center gap-2 mb-1 flex-wrap">
                                    <p class="text-sm font-semibold text-gray-900 truncate">
                                      {{ r.concept || 'Comprobante' }}
                                    </p>
                                    <span *ngIf="r.approvalStatus === 'PendingApproval'"
                                          class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">Pendiente</span>
                                    <span *ngIf="r.isManualPayment"
                                          class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">Manual</span>
                                    <span *ngIf="!r.isManualPayment"
                                          class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">Automático</span>
                                    <span *ngIf="r.isBankReconciled"
                                          class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800"
                                          title="Comprobante conciliado bancariamente">🔒 Conciliado</span>
                                  </div>
                                  <div class="text-xs text-gray-500 space-y-0.5">
                                    <p>
                                      <a [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                                         class="text-indigo-600 hover:underline font-medium">
                                        Comprobante #{{ r.receiptNumber || r.id }}
                                      </a>
                                    </p>
                                    <p *ngIf="r.transactionDate">Transacción: <strong>{{ r.transactionDate | date:'mediumDate' }}</strong></p>
                                    <p *ngIf="!r.transactionDate" class="text-gray-400 italic">Fecha transacción no disponible</p>
                                    <p *ngIf="r.createdAt">Creación: {{ r.createdAt | date:'mediumDate' }}</p>
                                    <p *ngIf="r.paymentMethod?.name">Método: <strong>{{ r.paymentMethod.name }}</strong></p>
                                  </div>
                                </div>
                                <div class="flex items-center gap-1 shrink-0">
                                  <a [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                                     class="text-[10px] text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded hover:bg-indigo-50">
                                    Ver
                                  </a>
                                  <button *ngIf="canCancelReceipt(r)"
                                          (click)="openCancelReceiptModal(r)"
                                          class="text-[10px] text-red-600 border border-red-200 px-2 py-0.5 rounded hover:bg-red-50"
                                          title="Anular comprobante">
                                    Anular
                                  </button>
                                </div>
                              </div>

                              <!-- Exchange rate card (internacional) -->
                              <div *ngIf="hasReceiptExchangeRate(r)"
                                   class="bg-purple-50 border border-purple-200 rounded-md p-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <div>
                                  <p class="text-[10px] text-purple-600 uppercase tracking-wider">Moneda</p>
                                  <p class="font-semibold text-purple-900">{{ r.currencyCode }}</p>
                                </div>
                                <div>
                                  <p class="text-[10px] text-purple-600 uppercase tracking-wider">Tasa</p>
                                  <p class="font-semibold text-purple-900 tabular-nums">{{ r.exchangeRate | number:'1.2-4' }}</p>
                                </div>
                                <div *ngIf="r.exchangeRateEffectiveDate" class="col-span-2 sm:col-span-2">
                                  <p class="text-[10px] text-purple-600 uppercase tracking-wider">Vigencia</p>
                                  <p class="text-purple-900">{{ r.exchangeRateEffectiveDate | date:'mediumDate' }}</p>
                                </div>
                              </div>

                              <!-- Detalle financiero: Bruto / Descuentos / Neto -->
                              <div class="grid grid-cols-3 gap-2 text-xs">
                                <div class="bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                  <p class="text-[10px] text-yellow-700 uppercase tracking-wider">Bruto</p>
                                  <p class="font-bold text-yellow-800 tabular-nums">
                                    {{ formatCurrencyValue(r.grossAmount, r.currencySymbol, r.currencyCode) }}
                                  </p>
                                  <p *ngIf="hasReceiptExchangeRate(r)" class="text-[10px] text-yellow-600 tabular-nums">
                                    ≈ {{ getReceiptCOPEquivalent(r, r.grossAmount) }}
                                  </p>
                                </div>
                                <div class="bg-red-50 border border-red-200 rounded-md p-2">
                                  <p class="text-[10px] text-red-700 uppercase tracking-wider">Descuentos</p>
                                  <p class="font-bold text-red-800 tabular-nums">
                                    {{ formatCurrencyValue(r.totalFees || 0, r.currencySymbol, r.currencyCode) }}
                                  </p>
                                  <p *ngIf="hasReceiptExchangeRate(r) && r.totalFees" class="text-[10px] text-red-600 tabular-nums">
                                    ≈ {{ getReceiptCOPEquivalent(r, r.totalFees) }}
                                  </p>
                                </div>
                                <div class="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                                  <p class="text-[10px] text-emerald-700 uppercase tracking-wider">Neto</p>
                                  <p class="font-bold text-emerald-800 tabular-nums">
                                    {{ formatCurrencyValue(r.netAmount, r.currencySymbol, r.currencyCode) }}
                                  </p>
                                  <p *ngIf="hasReceiptExchangeRate(r)" class="text-[10px] text-emerald-600 tabular-nums">
                                    ≈ {{ getReceiptCOPEquivalent(r, r.netAmount) }}
                                  </p>
                                </div>
                              </div>

                              <!-- Descuentos aplicados (feeDetails) -->
                              <div *ngIf="r.feeDetails?.length"
                                   class="border-t border-gray-100 pt-2">
                                <p class="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Descuentos aplicados</p>
                                <div class="space-y-1">
                                  <div *ngFor="let fee of r.feeDetails" class="flex items-center justify-between text-xs">
                                    <span class="text-gray-700">
                                      {{ fee.feeType?.name || fee.name || 'Descuento' }}
                                      <span *ngIf="fee.description" class="text-gray-500"> · {{ fee.description }}</span>
                                    </span>
                                    <span class="font-semibold text-gray-900 tabular-nums">
                                      {{ formatCurrencyValue(fee.appliedAmount || fee.amount || 0, r.currencySymbol, r.currencyCode) }}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <!-- Observaciones -->
                              <div *ngIf="r.observations"
                                   class="bg-blue-50 border border-blue-200 rounded-md p-2">
                                <p class="text-[10px] text-blue-700 uppercase tracking-wider mb-0.5">Observaciones</p>
                                <p class="text-xs text-blue-900 whitespace-pre-wrap">{{ r.observations }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Tab Anulados -->
                          <div *ngIf="getCompanyPlanTab(cuota.id) === 'cancelled' && getCancelledReceipts(cuota).length > 0"
                               class="space-y-2">
                            <div *ngFor="let r of getCancelledReceipts(cuota)"
                                 class="bg-white border border-red-200 rounded-md p-3 flex items-start justify-between gap-3">
                              <div class="min-w-0">
                                <p class="text-sm font-semibold text-red-800 line-through truncate">
                                  {{ r.concept || 'Comprobante' }}
                                </p>
                                <div class="text-xs text-gray-500 space-y-0.5 mt-1">
                                  <p>
                                    <a [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                                       class="text-red-700 hover:underline font-medium">
                                      Comprobante #{{ r.receiptNumber || r.id }} (anulado)
                                    </a>
                                  </p>
                                  <p *ngIf="r.createdAt">Creación: {{ r.createdAt | date:'mediumDate' }}</p>
                                  <p class="text-red-600" *ngIf="r.cancelledAt">Anulado: {{ r.cancelledAt | date:'mediumDate' }}</p>
                                  <p class="text-red-600" *ngIf="r.cancelledBy">Por: {{ r.cancelledBy }}</p>
                                  <p class="text-red-600" *ngIf="r.cancellationReason">Motivo: {{ r.cancellationReason }}</p>
                                </div>
                              </div>
                              <div class="text-right shrink-0">
                                <p class="text-sm font-bold text-red-700 line-through">
                                  {{ formatCurrencyValue(r.netAmount, r.currencySymbol, r.currencyCode) }}
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- Tab Devoluciones -->
                          <div *ngIf="getCompanyPlanTab(cuota.id) === 'refunds' && hasRefundsForCompanyPlan(cuota)"
                               class="space-y-2">
                            <div *ngFor="let refund of getRefundsForCompanyPlan(cuota)"
                                 class="bg-white border border-purple-200 rounded-md p-3 flex items-start justify-between gap-3">
                              <div class="min-w-0">
                                <div class="flex items-center gap-2 flex-wrap mb-0.5">
                                  <p class="text-sm font-semibold text-purple-700 truncate">
                                    Devolución {{ refund.documentNumber || ('#' + refund.id) }}
                                  </p>
                                  <span *ngIf="refund.statusDescription"
                                        class="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
                                    {{ refund.statusDescription }}
                                  </span>
                                </div>
                                <p class="text-xs text-gray-500">
                                  {{ (refund.documentDate || refund.createdAt) | date:'mediumDate' }}
                                  <span *ngIf="refund.refundConceptDescription"> · {{ refund.refundConceptDescription }}</span>
                                </p>
                                <p *ngIf="refund.rejectionReason" class="text-xs text-red-600">Rechazado: {{ refund.rejectionReason }}</p>
                                <p *ngIf="refund.cancellationReason" class="text-xs text-red-600">Anulado: {{ refund.cancellationReason }}</p>
                              </div>
                              <div class="text-right shrink-0">
                                <p class="text-sm font-bold text-purple-700">
                                  {{ formatRefundAmount(refund.totalRefundedForPlan || 0, refund) }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </ng-container>
                    <tr *ngIf="!details?.companyPaymentPlans?.length">
                      <td [attr.colspan]="(isCompanyPunishMode ? 10 : 9) + (isSuperAdmin ? 1 : 0) + 1"
                          class="px-4 py-6 text-center text-sm text-gray-500">Sin cuotas de empresa.</td>
                    </tr>
                  </tbody>
                  <tfoot class="bg-gray-50 border-t-2 border-gray-200"
                         *ngIf="details?.companyPaymentPlans?.length">
                    <tr>
                      <td *ngIf="isCompanyPunishMode"></td>
                      <td *ngIf="isSuperAdmin"></td>
                      <td></td>
                      <td colspan="3" class="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider text-right">
                        Totales ({{ details.companyPaymentPlans.length }} cuotas)
                      </td>
                      <td class="px-3 py-3 text-right tabular-nums" title="Cuotas asignadas vs matrícula">
                        <span class="block text-sm font-bold text-gray-900">{{ getCompanyTotalAmount() | currency:'COP':'symbol':'1.0-2' }}</span>
                        <span class="block text-xs font-normal text-gray-500">de {{ getCompanyExpectedTotal() | currency:'COP':'symbol':'1.0-2' }} matrícula</span>
                        <span *ngIf="getCompanyUnassignedAmount() > 0" class="block text-xs font-semibold text-amber-700">
                          Faltante: {{ getCompanyUnassignedAmount() | currency:'COP':'symbol':'1.0-2' }}
                        </span>
                      </td>
                      <td class="px-3 py-3 text-sm font-bold text-emerald-700 text-right tabular-nums"
                          title="Total pagado">
                        {{ getCompanyPaidAmount() | currency:'COP':'symbol':'1.0-2' }}
                      </td>
                      <td class="px-3 py-3 text-right tabular-nums" title="Saldo de cuotas asignadas vs pendiente de matrícula">
                        <span class="block text-sm font-bold text-amber-700">{{ (getCompanyTotalAmount() - getCompanyPaidAmount()) | currency:'COP':'symbol':'1.0-2' }}</span>
                        <span class="block text-xs font-normal text-gray-500">de {{ getCompanyExpectedPending() | currency:'COP':'symbol':'1.0-2' }} matrícula</span>
                        <span *ngIf="getCompanyUnassignedAmount() > 0" class="block text-xs font-semibold text-amber-700">
                          Faltante: {{ getCompanyUnassignedAmount() | currency:'COP':'symbol':'1.0-2' }}
                        </span>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr *ngIf="getLastPaymentDate(details?.companyPaymentPlans)" class="border-t border-gray-100">
                      <td *ngIf="isCompanyPunishMode"></td>
                      <td *ngIf="isSuperAdmin"></td>
                      <td colspan="9" class="px-3 py-2 text-xs text-gray-500 text-right">
                        <span class="inline-flex items-center gap-1.5">
                          <svg class="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Último pago recibido: <strong class="text-gray-700">{{ getLastPaymentDate(details?.companyPaymentPlans) | date:'mediumDate' }}</strong>
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <!-- Leyenda Estados -->
              <div class="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p class="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Leyenda de estados</p>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                  <span class="inline-flex items-center gap-1.5">
                    <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span class="text-gray-700">Pagado</span>
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span class="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span class="text-gray-700">Pendiente</span>
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span class="w-2 h-2 rounded-full bg-red-500"></span>
                    <span class="text-gray-700">Vencido</span>
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span class="text-gray-700">Link generado</span>
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span class="w-2 h-2 rounded-full bg-rose-600"></span>
                    <span class="text-gray-700">Castigada</span>
                  </span>
                </div>
              </div>

              <!-- Resumen Cartera Castigada Corporativa -->
              <div *ngIf="hasTotalPunishedInstallments()"
                   class="px-4 py-3 border-t border-rose-200 bg-rose-50">
                <div class="flex items-start gap-3">
                  <svg class="w-5 h-5 text-rose-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-rose-900">Cartera Castigada Corporativa</p>
                    <p class="text-xs text-rose-700 mt-0.5 tabular-nums">
                      <strong>{{ getCompanyPunishedInstallments().length }}</strong> cuota(s) castigadas
                      · Total castigado: <strong>{{ formatCurrencyValue(getTotalPunishedAmount()) }}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- Tab Participantes                                             -->
      <!-- ============================================================ -->
      <section *ngIf="activeTab === 'participantes'">
        <!-- KPIs (6) -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Total Participantes</p>
            <p class="text-lg font-semibold text-gray-900 mt-1">{{ participantsSummary.total }}</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Activos / Retirados</p>
            <p class="text-lg font-semibold text-gray-900 mt-1">{{ participantsSummary.active }} / {{ participantsSummary.withdrawn }}</p>
            <p class="text-xs text-gray-500" *ngIf="participantsSummary.postponed > 0">+{{ participantsSummary.postponed }} aplazados</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Cuotas Pagadas</p>
            <p class="text-lg font-semibold text-gray-900 mt-1">{{ participantsPaidInstallmentsCount }} / {{ participantsTotalInstallmentsCount }}</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">% Recaudo</p>
            <p class="text-lg font-semibold text-gray-900 mt-1">{{ participantsRecaudoPercentage }}%</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Saldo Participantes</p>
            <p class="text-lg font-semibold text-gray-900 mt-1">{{ participantsPendingBalance | currency:'COP':'symbol':'1.0-2' }}</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <p class="text-xs text-gray-500">Próximo Vencimiento</p>
            <p class="text-sm font-medium text-gray-900 mt-1" *ngIf="participantsNextDue as next">
              {{ next.dueDate | date:'yyyy-MM-dd' }}
            </p>
            <p class="text-sm text-gray-400 mt-1" *ngIf="!participantsNextDue">—</p>
          </div>
        </div>

        <!-- Toolbar: search + filter + sort -->
        <div class="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap items-center gap-3">
          <div class="flex-1 min-w-[200px]">
            <input
              type="search"
              [(ngModel)]="participantSearch"
              placeholder="Buscar por nombre o documento"
              class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            />
          </div>
          <select [(ngModel)]="participantStatusFilter"
                  class="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="withdrawn">Retirados</option>
            <option value="postponed">Aplazados</option>
          </select>
          <select [(ngModel)]="participantSort"
                  class="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="balance-desc">Mayor saldo</option>
            <option value="name-asc">Nombre A-Z</option>
            <option value="recaudo-desc">Mayor % recaudo</option>
            <option value="next-due-asc">Próximo vencimiento</option>
          </select>
        </div>

        <!-- Tabla -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-gray-50">
              <tr class="text-xs text-gray-500 uppercase">
                <th class="w-8"></th>
                <th class="px-4 py-2 text-left">Participante</th>
                <th class="px-4 py-2 text-left">Estado</th>
                <th class="px-4 py-2 text-right">Pagado / Total</th>
                <th class="px-4 py-2 text-right">% Recaudo</th>
                <th class="px-4 py-2 text-left">Próx Vto</th>
                <th class="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <ng-container *ngFor="let pe of filteredParticipants; trackBy: trackParticipantById">
                <tr class="hover:bg-gray-50 cursor-pointer" (click)="toggleParticipant(participantId(pe))">
                  <td class="px-2 text-gray-400">
                    <svg class="w-4 h-4 transition-transform"
                         [class.rotate-90]="expandedParticipantId === participantId(pe)"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2 min-w-0">
                      <div class="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs shrink-0"
                           [title]="participantFullName(pe)">
                        {{ (participantFullName(pe) || '?').charAt(0) }}{{ participantFullName(pe).split(' ')[1]?.charAt(0) || '' }}
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">{{ participantFullName(pe) || 'Sin nombre' }}</p>
                        <p class="text-xs text-gray-500 truncate">{{ participantDocument(pe) }}</p>
                        <p *ngIf="participantEmail(pe)" class="text-[11px] text-gray-400 truncate">{{ participantEmail(pe) }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <ng-container *ngIf="(pe.paymentPlans || []).length > 0; else empresaCubre100">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        Inscrito
                      </span>
                    </ng-container>
                    <ng-template #empresaCubre100>
                      <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            title="Este participante no tiene cuotas propias — la empresa cubre el 100% del costo">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Empresa cubre 100%
                      </span>
                    </ng-template>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900">
                    <ng-container *ngIf="(pe.paymentPlans || []).length > 0; else sinCuotasAsignadas">
                      {{ participantTotalPaid(pe) | currency:'COP':'symbol':'1.0-2' }}
                      <span class="text-gray-400">/</span>
                      {{ getParticipantExpectedTotal(pe) | currency:'COP':'symbol':'1.0-2' }}
                    </ng-container>
                    <ng-template #sinCuotasAsignadas>
                      <span class="text-xs text-gray-400 italic">Sin cuotas asignadas</span>
                    </ng-template>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-900">
                    <ng-container *ngIf="(pe.paymentPlans || []).length > 0; else cubiertoPorEmpresa">
                      {{ recaudoForParticipant(pe) }}%
                    </ng-container>
                    <ng-template #cubiertoPorEmpresa>
                      <span class="text-xs text-blue-600 font-medium">Cubierto por empresa</span>
                    </ng-template>
                  </td>
                  <td class="px-4 py-3 text-gray-700">
                    <ng-container *ngIf="nextDueForParticipant(pe) as nd; else sinPagosPendientes">
                      {{ nd.dueDate | date:'yyyy-MM-dd' }}
                    </ng-container>
                    <ng-template #sinPagosPendientes>
                      <span class="text-xs text-gray-400 italic">Sin pagos pendientes</span>
                    </ng-template>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <div class="inline-flex items-center gap-2" (click)="$event.stopPropagation()">
                      <span *ngIf="!(pe.paymentPlans || []).length"
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200"
                            title="Este participante no tiene cuotas propias">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Sin cuotas propias
                      </span>
                      <a [routerLink]="['/dashboard/participants/profile-participant', participantId(pe)]"
                         target="_blank"
                         class="text-xs text-blue-600 hover:underline">Perfil ↗</a>
                    </div>
                  </td>
                </tr>
                <tr *ngIf="expandedParticipantId === participantId(pe)">
                  <td colspan="7" class="bg-gray-50 p-4">
                    <!-- Mini KPIs participante (5) -->
                    <div class="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <p class="text-xs text-gray-500">Total</p>
                        <p class="text-sm font-semibold text-gray-900">{{ getParticipantExpectedTotal(pe) | currency:'COP':'symbol':'1.0-2' }}</p>
                        <p *ngIf="getParticipantUnassignedAmount(pe) > 0"
                           class="text-xs font-semibold text-yellow-700 mt-1">
                          Faltante por reasignar: {{ getParticipantUnassignedAmount(pe) | currency:'COP':'symbol':'1.0-2' }}
                        </p>
                      </div>
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <p class="text-xs text-gray-500">Pagado</p>
                        <p class="text-sm font-semibold text-emerald-700">{{ participantTotalPaid(pe) | currency:'COP':'symbol':'1.0-2' }}</p>
                      </div>
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <p class="text-xs text-gray-500">Saldo</p>
                        <p class="text-sm font-semibold text-gray-900">{{ getParticipantExpectedPending(pe) | currency:'COP':'symbol':'1.0-2' }}</p>
                      </div>
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <p class="text-xs text-gray-500">Vencido</p>
                        <p class="text-sm font-semibold text-red-700">{{ overdueForParticipant(pe) | currency:'COP':'symbol':'1.0-2' }}</p>
                      </div>
                      <div class="bg-white border border-gray-200 rounded-lg p-3">
                        <p class="text-xs text-gray-500">Cartera castigada</p>
                        <p class="text-sm font-semibold text-gray-900">{{ punishedForParticipant(pe) | currency:'COP':'symbol':'1.0-2' }}</p>
                      </div>
                    </div>

                    <!-- Plan del participante -->
                    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <!-- Toolbar -->
                      <div class="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 class="text-sm font-semibold text-gray-900">Plan de pagos — {{ participantFullName(pe) }}</h3>
                          <p class="text-xs text-gray-500">{{ (pe.paymentPlans || []).length }} cuotas</p>
                        </div>
                        <div class="flex flex-wrap items-center gap-2">
                          <button (click)="goToEditParticipantPlans(pe)"
                                  [disabled]="recalculatingParticipantId === pe?.enrollment?.participant?.id"
                                  [title]="'Recalcula los pagos del participante y abre el editor del plan'"
                                  class="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition"
                                  [ngClass]="recalculatingParticipantId === pe?.enrollment?.participant?.id
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'">
                            {{ recalculatingParticipantId === pe?.enrollment?.participant?.id ? 'Recalculando…' : 'Editar plan' }}
                          </button>
                          <a [routerLink]="['/dashboard/enrollments/payment-plans/history', participantId(pe)]"
                             [queryParams]="{ type: 'corporate-participant', agreementId: agreementId }"
                             target="_blank"
                             class="inline-flex items-center gap-1.5 text-xs text-gray-700 border border-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-50">
                            Historial
                          </a>
                          <button *ngIf="(pe.paymentPlans || []).length && !isEnrollmentCancelled"
                                  (click)="selectedParticipant = pe; toggleParticipantPunishMode()"
                                  class="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition"
                                  [ngClass]="(isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id)
                                    ? 'bg-red-100 ring-2 ring-red-400 border-red-200 text-red-700 hover:bg-red-50'
                                    : 'border-red-200 text-red-700 hover:bg-red-50'">
                            {{ (isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id) ? 'Cancelar gestión' : 'Gestionar castigo' }}
                          </button>
                        </div>
                      </div>

                      <!-- Panel de gestión castigo (Participante) -->
                      <div *ngIf="isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id"
                           class="px-4 py-3 bg-red-50 border-b border-red-200">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label class="block text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">
                              Razón del cambio *
                            </label>
                            <input [(ngModel)]="participantPunishReason" type="text"
                                   class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                                   placeholder="Obligatoria">
                          </div>
                          <div>
                            <label class="block text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">
                              Notas adicionales
                            </label>
                            <input [(ngModel)]="participantPunishNotes" type="text"
                                   class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                                   placeholder="Opcional">
                          </div>
                        </div>
                        <div class="flex items-center justify-between flex-wrap gap-2">
                          <div class="flex items-center gap-3 text-xs text-gray-700">
                            <button (click)="selectAllParticipantPlans()"
                                    class="text-xs text-red-700 underline hover:text-red-900">
                              Seleccionar todas
                            </button>
                            <span>
                              <span class="font-bold text-red-600">{{ selectedParticipantPlanIds.length }}</span> cuotas seleccionadas
                            </span>
                          </div>
                          <div class="flex gap-2">
                            <button (click)="executeParticipantPunishAction('punish')"
                                    [disabled]="selectedParticipantPlanIds.length === 0 || !participantPunishReason"
                                    class="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-xs font-medium">
                              Castigar
                            </button>
                            <button (click)="executeParticipantPunishAction('unpunish')"
                                    [disabled]="selectedParticipantPlanIds.length === 0 || !participantPunishReason"
                                    class="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 text-xs font-medium">
                              Descastigar
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Último pago recibido (participante) -->
                      <div *ngIf="getLastPaymentDate(pe.paymentPlans)"
                           class="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-xs text-emerald-800">
                        <span class="inline-flex items-center gap-1.5">
                          <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Último pago recibido: <strong>{{ getLastPaymentDate(pe.paymentPlans) | date:'mediumDate' }}</strong>
                        </span>
                      </div>

                      <!-- Tabla de cuotas del participante -->
                      <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                          <thead class="bg-white">
                            <tr class="text-xs text-gray-500 uppercase">
                              <th class="px-2 py-2 w-8" *ngIf="isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id"></th>
                              <th class="px-2 py-2 w-8" *ngIf="isSuperAdmin"
                                  title="Arrastrá para reordenar las cuotas"></th>
                              <th class="px-2 py-2 w-8"></th>
                              <th class="px-2 py-2 text-left">Cuota</th>
                              <th class="px-2 py-2 text-left" title="PaymentPlan.Id en DB">ID</th>
                              <th class="px-3 py-2 text-left">Fecha</th>
                              <th class="px-3 py-2 text-right">Monto</th>
                              <th class="px-3 py-2 text-right">Pagado</th>
                              <th class="px-3 py-2 text-right" title="Monto pendiente = Monto - Pagado">Saldo Restante</th>
                              <th class="px-3 py-2 text-left">Estado</th>
                              <th class="px-3 py-2 text-right">Acciones</th>
                            </tr>
                          </thead>
                          <tbody class="divide-y divide-gray-100"
                                 cdkDropList
                                 [cdkDropListDisabled]="!isSuperAdmin || (isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id)"
                                 (cdkDropListDropped)="selectedParticipant = pe; onParticipantPlanDrop($event)">
                            <ng-container *ngFor="let cuota of pe.paymentPlans">
                              <tr cdkDrag
                                  [cdkDragData]="cuota"
                                  [cdkDragDisabled]="!isSuperAdmin || (isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id)"
                                  cdkDragLockAxis="y"
                                  class="hover:bg-gray-50"
                                  [class.bg-red-50]="cuota?.isPunished">
                                <td *ngIf="isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id"
                                    class="px-2 py-2">
                                  <input type="checkbox"
                                         [checked]="isParticipantPlanSelected(cuota.id)"
                                         [disabled]="cuota.paymentStatus === 'PAID'"
                                         (change)="toggleParticipantPlanSelection(cuota.id)"
                                         class="rounded border-gray-300 text-red-600 focus:ring-red-500 h-4 w-4 disabled:opacity-50">
                                </td>
                                <td *ngIf="isSuperAdmin"
                                    cdkDragHandle
                                    class="px-2 py-2 text-center text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                                    title="Arrastrar para reordenar">
                                  <svg class="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z"/>
                                  </svg>
                                </td>
                                <td class="px-2 py-2 text-center">
                                  <button (click)="toggleCollapse(cuota)"
                                          class="text-gray-400 hover:text-indigo-600 transition-colors"
                                          [title]="cuota.isCollapsed === false ? 'Colapsar' : 'Ver comprobantes'">
                                    <svg class="w-4 h-4 transition-transform" [ngClass]="{'rotate-180': cuota.isCollapsed === false}"
                                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </td>
                                <td class="px-2 py-2 text-gray-700 font-medium">{{ cuota.installmentNumber }}</td>
                                <td class="px-2 py-2 font-mono text-[11px] text-gray-500" title="PaymentPlan.Id">#{{ cuota.id }}</td>
                                <td class="px-3 py-2 text-gray-900">
                                  {{ cuota.dueDate | date:'yyyy-MM-dd' }}
                                  <p *ngIf="isPaymentOverdue(cuota) && !cuota.isPunished" class="text-[10px] text-red-500">
                                    Vencida hace {{ getDaysOverdue(cuota) }} días
                                  </p>
                                </td>
                                <td class="px-3 py-2 text-right text-gray-900">
                                  {{ cuota.amountToPay | currency:'COP':'symbol':'1.0-2' }}
                                  <span *ngIf="isInternationalEnrollment && !cuota.isPunished"
                                        class="block text-[10px] text-purple-600">
                                    ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountToPay) }}
                                  </span>
                                </td>
                                <td class="px-3 py-2 text-right text-emerald-700">
                                  {{ cuota.amountPaid | currency:'COP':'symbol':'1.0-2' }}
                                  <span *ngIf="isInternationalEnrollment && cuota.amountPaid > 0 && !cuota.isPunished"
                                        class="block text-[10px] text-purple-600">
                                    ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountPaid) }}
                                  </span>
                                </td>
                                <td class="px-3 py-2 text-right tabular-nums" [ngClass]="{
                                  'text-gray-400 italic': cuota.isPunished,
                                  'text-emerald-600 font-medium': !cuota.isPunished && (cuota.amountToPay - cuota.amountPaid) <= 0,
                                  'text-rose-600 font-medium': !cuota.isPunished && isPaymentOverdue(cuota) && (cuota.amountToPay - cuota.amountPaid) > 0,
                                  'text-gray-700': !cuota.isPunished && !isPaymentOverdue(cuota) && (cuota.amountToPay - cuota.amountPaid) > 0
                                }">
                                  {{ (cuota.amountToPay - cuota.amountPaid) | currency:'COP':'symbol':'1.0-2' }}
                                  <span *ngIf="isInternationalEnrollment && !cuota.isPunished && (cuota.amountToPay - cuota.amountPaid) > 0"
                                        class="block text-[10px] text-purple-600">
                                    ≈ {{ calculatePlanCOPEquivalent(cuota, cuota.amountToPay - cuota.amountPaid) }}
                                  </span>
                                </td>
                                <td class="px-3 py-2">
                                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                        [ngClass]="getPaymentStatusClass(cuota.paymentStatus)">
                                    {{ getPaymentStatusLabel(cuota.paymentStatus) }}
                                  </span>
                                  <span *ngIf="cuota.isPunished"
                                        class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700">
                                    Castigada
                                  </span>
                                </td>
                                <td class="px-3 py-2 text-right">
                                  <div class="inline-flex items-center gap-1">
                                    <a *ngIf="cuota.paymentStatus !== 'PAID' && !cuota.isPunished && !isEnrollmentCancelled"
                                       [routerLink]="['/dashboard/income-receipts/new']"
                                       [queryParams]="{participantId: participantId(pe), companyId: details.company?.id, projectId: details.project?.id, corporateEnrollmentId: details.id, installmentNumber: cuota.installmentNumber, isParticipantCorporatePayment: true}"
                                       class="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md font-bold transition"
                                       title="Registrar pago de esta cuota">
                                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                                      </svg>
                                      Pagar
                                    </a>
                                    <button *ngIf="isSuperAdmin"
                                            (click)="openAdminMenu(cuota)"
                                            class="text-xs text-purple-700 border border-purple-200 px-2 py-1 rounded-md hover:bg-purple-50">
                                      Admin
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              <!-- Fila colapsable participante: comprobantes / anulados / devoluciones -->
                              <tr *ngIf="cuota.isCollapsed === false" class="bg-gray-50 border-t border-gray-100">
                                <td [attr.colspan]="((isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id) ? 10 : 9) + (isSuperAdmin ? 1 : 0)" class="px-4 py-3">
                                  <!-- Castigada info card -->
                                  <div *ngIf="cuota.isPunished"
                                       class="bg-rose-50 border border-rose-200 rounded-md p-3 mb-3">
                                    <div class="flex items-start gap-2">
                                      <svg class="w-4 h-4 text-rose-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                      </svg>
                                      <div class="flex-1 min-w-0">
                                        <p class="text-sm font-semibold text-rose-900 mb-1">Cartera Castigada</p>
                                        <div class="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                          <div>
                                            <p class="text-[10px] text-rose-600 uppercase tracking-wider">Monto castigado</p>
                                            <p class="font-bold text-rose-800 tabular-nums">
                                              {{ formatCurrencyValue(cuota.amountToPay - cuota.amountPaid) }}
                                            </p>
                                          </div>
                                          <div *ngIf="cuota.punishedAt">
                                            <p class="text-[10px] text-rose-600 uppercase tracking-wider">Fecha</p>
                                            <p class="text-rose-800">{{ cuota.punishedAt | date:'mediumDate' }}</p>
                                          </div>
                                          <div *ngIf="cuota.punishedBy">
                                            <p class="text-[10px] text-rose-600 uppercase tracking-wider">Por</p>
                                            <p class="text-rose-800">{{ cuota.punishedBy }}</p>
                                          </div>
                                        </div>
                                        <p *ngIf="cuota.punishReason" class="text-xs text-rose-800 mt-1.5">
                                          <strong>Razón:</strong> {{ cuota.punishReason }}
                                        </p>
                                        <p *ngIf="cuota.punishNotes" class="text-xs text-rose-700 mt-0.5 italic">
                                          {{ cuota.punishNotes }}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- Observaciones de la cuota -->
                                  <div *ngIf="cuota.observations"
                                       class="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                                    <p class="text-[10px] text-blue-700 uppercase tracking-wider mb-1">Observaciones</p>
                                    <p class="text-sm text-blue-900 whitespace-pre-wrap">{{ cuota.observations }}</p>
                                  </div>

                                  <div class="border-b border-gray-200 mb-3">
                                    <nav class="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                                      <button *ngIf="getActiveReceipts(cuota).length > 0"
                                              (click)="setParticipantPlanTab(cuota.id, 'active')"
                                              [class]="getParticipantPlanTab(cuota.id) === 'active'
                                                ? 'border-emerald-500 text-emerald-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5">
                                        Comprobantes
                                        <span class="bg-emerald-100 text-emerald-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                          {{ getActiveReceipts(cuota).length }}
                                        </span>
                                      </button>
                                      <button *ngIf="getCancelledReceipts(cuota).length > 0"
                                              (click)="setParticipantPlanTab(cuota.id, 'cancelled')"
                                              [class]="getParticipantPlanTab(cuota.id) === 'cancelled'
                                                ? 'border-red-500 text-red-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5">
                                        Anulados
                                        <span class="bg-red-100 text-red-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                          {{ getCancelledReceipts(cuota).length }}
                                        </span>
                                      </button>
                                      <button *ngIf="hasRefundsForParticipantPlan(cuota)"
                                              (click)="setParticipantPlanTab(cuota.id, 'refunds')"
                                              [class]="getParticipantPlanTab(cuota.id) === 'refunds'
                                                ? 'border-purple-500 text-purple-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5">
                                        Devoluciones
                                      </button>
                                      <button (click)="setParticipantPlanTab(cuota.id, 'emails'); loadEmailsForPlan(cuota)"
                                              [class]="getParticipantPlanTab(cuota.id) === 'emails'
                                                ? 'border-sky-500 text-sky-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs flex items-center gap-1.5">
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                        </svg>
                                        Emails
                                        <span *ngIf="hasEmails(cuota)" class="bg-sky-100 text-sky-700 py-0.5 px-1.5 rounded-full text-[10px] font-bold">
                                          {{ getEmailsForPlan(cuota).length }}
                                        </span>
                                      </button>
                                      <span *ngIf="!getActiveReceipts(cuota).length && !getCancelledReceipts(cuota).length && !hasRefundsForParticipantPlan(cuota) && !hasEmails(cuota)"
                                            class="text-xs text-gray-500 py-2 ml-2">
                                        Sin comprobantes ni movimientos registrados para esta cuota.
                                      </span>
                                    </nav>
                                  </div>

                                  <!-- Tab Emails (participante) -->
                                  <div *ngIf="getParticipantPlanTab(cuota.id) === 'emails'" class="space-y-2">
                                    <div *ngIf="isEmailsLoading(cuota)" class="text-center py-4 text-xs text-gray-500">
                                      Cargando emails...
                                    </div>
                                    <div *ngIf="!isEmailsLoading(cuota) && !hasEmails(cuota)" class="text-center py-4 text-xs text-gray-500">
                                      Sin emails registrados para esta cuota.
                                    </div>
                                    <div *ngFor="let email of getEmailsForPlan(cuota)"
                                         class="bg-white border border-gray-200 rounded-md p-3">
                                      <div class="flex items-start justify-between gap-3 mb-1.5">
                                        <div class="flex items-center gap-2 flex-wrap min-w-0">
                                          <span class="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                                                [ngClass]="emailLogService.getEmailTypeClass(email.emailType)">
                                            {{ emailLogService.getEmailTypeLabel(email.emailType) }}
                                          </span>
                                          <span class="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                                                [ngClass]="emailLogService.getSuccessClass(email.isSuccess)">
                                            {{ email.isSuccess ? 'Entregado' : 'Falló' }}
                                          </span>
                                        </div>
                                        <span class="text-[10px] text-gray-500 shrink-0 tabular-nums">{{ email.sentAt | date:'dd/MM/yy HH:mm' }}</span>
                                      </div>
                                      <p class="text-xs font-medium text-gray-900 truncate">{{ email.subject }}</p>
                                      <p class="text-xs text-gray-500 truncate">
                                        Para: {{ email.recipientName || email.recipientEmail }}
                                        <span *ngIf="email.recipientName" class="text-gray-400"> &lt;{{ email.recipientEmail }}&gt;</span>
                                      </p>
                                      <p *ngIf="!email.isSuccess && email.errorMessage"
                                         class="mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                                        Error: {{ email.errorMessage }}
                                      </p>
                                    </div>
                                  </div>

                                  <div *ngIf="getParticipantPlanTab(cuota.id) === 'active' && getActiveReceipts(cuota).length > 0"
                                       class="space-y-2">
                                    <div *ngFor="let r of getActiveReceipts(cuota)"
                                         class="bg-white border border-gray-200 rounded-md p-3 flex items-start justify-between gap-3">
                                      <div class="min-w-0">
                                        <a [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                                           class="text-sm font-semibold text-indigo-600 hover:underline truncate">
                                          Comprobante #{{ r.receiptNumber || r.id }}
                                        </a>
                                        <p class="text-xs text-gray-500">
                                          {{ (r.transactionDate || r.createdAt) | date:'mediumDate' }}
                                          <span *ngIf="r.paymentMethod"> · {{ r.paymentMethod }}</span>
                                        </p>
                                        <p class="text-xs text-gray-500" *ngIf="r.concept">{{ r.concept }}</p>
                                      </div>
                                      <div class="text-right shrink-0">
                                        <p class="text-sm font-bold text-emerald-700">
                                          {{ (r.netAmount ?? r.totalAmount ?? r.amount) | currency:'COP':'symbol':'1.0-2' }}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  <div *ngIf="getParticipantPlanTab(cuota.id) === 'cancelled' && getCancelledReceipts(cuota).length > 0"
                                       class="space-y-2">
                                    <div *ngFor="let r of getCancelledReceipts(cuota)"
                                         class="bg-white border border-red-200 rounded-md p-3 flex items-start justify-between gap-3">
                                      <div class="min-w-0">
                                        <a [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                                           class="text-sm font-semibold text-red-700 hover:underline">
                                          Comprobante #{{ r.receiptNumber || r.id }} (anulado)
                                        </a>
                                        <p class="text-xs text-gray-500">
                                          {{ (r.transactionDate || r.createdAt) | date:'mediumDate' }}
                                        </p>
                                        <p class="text-xs text-red-600" *ngIf="r.cancellationReason">
                                          Motivo: {{ r.cancellationReason }}
                                        </p>
                                      </div>
                                      <div class="text-right shrink-0">
                                        <p class="text-sm font-bold text-red-700 line-through">
                                          {{ (r.netAmount ?? r.totalAmount ?? r.amount) | currency:'COP':'symbol':'1.0-2' }}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  <div *ngIf="getParticipantPlanTab(cuota.id) === 'refunds' && hasRefundsForParticipantPlan(cuota)"
                                       class="space-y-2">
                                    <div *ngFor="let refund of getRefundsForParticipantPlan(cuota)"
                                         class="bg-white border border-purple-200 rounded-md p-3 flex items-start justify-between gap-3">
                                      <div class="min-w-0">
                                        <p class="text-sm font-semibold text-purple-700">
                                          Devolución #{{ refund.id }}
                                        </p>
                                        <p class="text-xs text-gray-500">
                                          {{ refund.createdAt | date:'mediumDate' }}
                                          <span *ngIf="refund.reason"> · {{ refund.reason }}</span>
                                        </p>
                                      </div>
                                      <div class="text-right shrink-0">
                                        <p class="text-sm font-bold text-purple-700">
                                          {{ formatRefundAmount(refund.totalRefundedForPlan || 0, refund) }}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </ng-container>
                            <tr *ngIf="!(pe.paymentPlans || []).length">
                              <td [attr.colspan]="((isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id) ? 10 : 9) + (isSuperAdmin ? 1 : 0)"
                                  class="px-4 py-4 text-center text-sm text-gray-500">Sin cuotas registradas.</td>
                            </tr>
                          </tbody>
                          <tfoot class="bg-gray-50 border-t-2 border-gray-200"
                                 *ngIf="(pe.paymentPlans || []).length">
                            <tr>
                              <td *ngIf="isParticipantPunishMode && selectedParticipant?.enrollment?.id === pe.enrollment?.id"></td>
                              <td *ngIf="isSuperAdmin"></td>
                              <td></td>
                              <td colspan="3" class="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider text-right">
                                Totales ({{ pe.paymentPlans.length }} cuotas)
                              </td>
                              <td class="px-3 py-3 text-sm font-bold text-gray-900 text-right tabular-nums"
                                  title="Total asignado">
                                {{ getParticipantExpectedTotal(pe) | currency:'COP':'symbol':'1.0-2' }}
                              </td>
                              <td class="px-3 py-3 text-sm font-bold text-emerald-700 text-right tabular-nums"
                                  title="Total pagado">
                                {{ participantTotalPaid(pe) | currency:'COP':'symbol':'1.0-2' }}
                              </td>
                              <td class="px-3 py-3 text-sm font-bold text-amber-700 text-right tabular-nums"
                                  title="Saldo pendiente">
                                {{ getParticipantExpectedPending(pe) | currency:'COP':'symbol':'1.0-2' }}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </ng-container>
              <tr *ngIf="filteredParticipants.length === 0">
                <td colspan="7" class="p-8 text-center text-sm text-gray-500">
                  Sin participantes que coincidan con los filtros.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- Tab Ingresos Generales                                        -->
      <!-- ============================================================ -->
      <section *ngIf="activeTab === 'ingresos'">
        <!-- Card resumen -->
        <div class="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex gap-6 flex-wrap">
              <div>
                <p class="text-xs text-gray-500">Total ingresos generales</p>
                <p class="text-lg font-semibold text-gray-900">{{ generalIncomesTotalAmount | currency:'COP':'symbol':'1.0-2' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Suma neta</p>
                <p class="text-lg font-semibold text-emerald-700">{{ generalIncomesNetAmount | currency:'COP':'symbol':'1.0-2' }}</p>
              </div>
              <div *ngIf="generalIncomesLastDate">
                <p class="text-xs text-gray-500">Última fecha</p>
                <p class="text-sm font-medium text-gray-900">{{ generalIncomesLastDate | date:'yyyy-MM-dd' }}</p>
              </div>
            </div>
            <button *ngIf="canAddGeneralIncome" (click)="openNewGeneralIncome()"
                    class="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
              + Agregar ingreso general
            </button>
          </div>
        </div>

        <!-- Lista rica de ingresos generales -->
        <div *ngIf="generalIncomeReceipts.length > 0" class="space-y-3">
          <div *ngFor="let r of generalIncomeReceipts"
               class="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <!-- Header: concept + badges + monto neto destacado -->
            <div class="flex items-start justify-between gap-4 flex-wrap">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                  <h3 class="text-base font-semibold text-gray-900 truncate">
                    {{ r.concept || r.description || 'Comprobante' }}
                  </h3>
                  <span *ngIf="r.isManualPayment"
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">Manual</span>
                  <span *ngIf="r.isManualPayment === false"
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">Automático</span>
                  <span *ngIf="getReceiptPayerType(r)"
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                    {{ getReceiptPayerType(r) }}
                  </span>
                  <span *ngIf="r.isBankReconciled"
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800"
                        title="Conciliado bancariamente">🔒 Conciliado</span>
                </div>
                <div class="text-xs text-gray-500 space-y-0.5">
                  <p *ngIf="getReceiptPayerLabel(r) as payer">Pagador: <strong class="text-gray-700">{{ payer }}</strong></p>
                  <p>Creación: {{ (r.receiptDate || r.issuedAt || r.createdAt) | date:'mediumDate' }}</p>
                  <p *ngIf="r.transactionDate">Transacción: {{ r.transactionDate | date:'mediumDate' }}</p>
                  <p *ngIf="r.paymentMethod?.name">Método: <strong>{{ r.paymentMethod.name }}</strong></p>
                  <p *ngIf="r.bankName || r.bank">Banco: {{ r.bankName || r.bank }}</p>
                  <p *ngIf="r.referenceNumber">Ref: <span class="font-mono">{{ r.referenceNumber }}</span></p>
                </div>
              </div>
              <div class="text-right shrink-0">
                <p class="text-[10px] text-gray-500 uppercase tracking-wider">Monto neto</p>
                <p class="text-2xl font-bold text-emerald-700 tabular-nums">
                  {{ formatCurrencyValue(r.netAmount, r.currencySymbol, r.currencyCode) }}
                </p>
                <a *ngIf="r.id" [routerLink]="['/dashboard/income-receipts/view', r.id]" target="_blank"
                   class="inline-block mt-1 text-xs text-indigo-600 hover:underline">
                  Ver comprobante completo ↗
                </a>
              </div>
            </div>

            <!-- Exchange rate card (internacional) -->
            <div *ngIf="hasReceiptExchangeRate(r)"
                 class="bg-purple-50 border border-purple-200 rounded-md p-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <p class="text-[10px] text-purple-600 uppercase tracking-wider">Moneda original</p>
                <p class="font-semibold text-purple-900">{{ r.currencyCode }}</p>
              </div>
              <div>
                <p class="text-[10px] text-purple-600 uppercase tracking-wider">Tasa</p>
                <p class="font-semibold text-purple-900 tabular-nums">{{ r.exchangeRate | number:'1.2-4' }}</p>
              </div>
              <div>
                <p class="text-[10px] text-purple-600 uppercase tracking-wider">Bruto (COP)</p>
                <p class="font-semibold text-purple-900 tabular-nums">{{ getReceiptCOPEquivalent(r, r.grossAmount) }}</p>
              </div>
              <div>
                <p class="text-[10px] text-purple-600 uppercase tracking-wider">Neto (COP)</p>
                <p class="font-semibold text-purple-900 tabular-nums">{{ getReceiptCOPEquivalent(r, r.netAmount) }}</p>
              </div>
              <div *ngIf="r.exchangeRateEffectiveDate" class="col-span-2 sm:col-span-4">
                <p class="text-[10px] text-purple-600 uppercase tracking-wider">Vigencia</p>
                <p class="text-purple-900">{{ r.exchangeRateEffectiveDate | date:'mediumDate' }}</p>
              </div>
            </div>

            <!-- Detalles financieros: Bruto / Descuentos / Neto / Banco -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div class="bg-yellow-50 border border-yellow-200 rounded-md p-2">
                <p class="text-[10px] text-yellow-700 uppercase tracking-wider">Bruto</p>
                <p class="font-bold text-yellow-800 tabular-nums">
                  {{ formatCurrencyValue(r.grossAmount, r.currencySymbol, r.currencyCode) }}
                </p>
              </div>
              <div class="bg-red-50 border border-red-200 rounded-md p-2">
                <p class="text-[10px] text-red-700 uppercase tracking-wider">Descuentos</p>
                <p class="font-bold text-red-800 tabular-nums">
                  {{ formatCurrencyValue(r.totalFees || 0, r.currencySymbol, r.currencyCode) }}
                </p>
              </div>
              <div class="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                <p class="text-[10px] text-emerald-700 uppercase tracking-wider">Neto</p>
                <p class="font-bold text-emerald-800 tabular-nums">
                  {{ formatCurrencyValue(r.netAmount, r.currencySymbol, r.currencyCode) }}
                </p>
              </div>
              <div class="bg-gray-50 border border-gray-200 rounded-md p-2" *ngIf="r.bankName || r.bank">
                <p class="text-[10px] text-gray-600 uppercase tracking-wider">Banco</p>
                <p class="font-semibold text-gray-900 truncate">{{ r.bankName || r.bank }}</p>
              </div>
            </div>

            <!-- Descuentos aplicados (feeDetails) -->
            <div *ngIf="r.feeDetails?.length" class="border-t border-gray-100 pt-2">
              <p class="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Descuentos aplicados</p>
              <div class="space-y-1">
                <div *ngFor="let fee of r.feeDetails" class="flex items-center justify-between text-xs">
                  <span class="text-gray-700">
                    {{ fee.feeType?.name || fee.name || 'Descuento' }}
                    <span *ngIf="fee.description" class="text-gray-500"> · {{ fee.description }}</span>
                  </span>
                  <span class="font-semibold text-gray-900 tabular-nums">
                    {{ formatCurrencyValue(fee.appliedAmount || fee.amount || 0, r.currencySymbol, r.currencyCode) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Observaciones -->
            <div *ngIf="r.observations" class="bg-blue-50 border border-blue-200 rounded-md p-2">
              <p class="text-[10px] text-blue-700 uppercase tracking-wider mb-0.5">Observaciones</p>
              <p class="text-xs text-blue-900 whitespace-pre-wrap">{{ r.observations }}</p>
            </div>
          </div>
        </div>

        <!-- Estado vacío rico -->
        <div *ngIf="generalIncomeReceipts.length === 0"
             class="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p class="text-sm text-gray-500 mb-3">Sin ingresos generales registrados.</p>
          <button *ngIf="canAddGeneralIncome" (click)="openNewGeneralIncome()"
                  class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
            + Agregar Primer Ingreso
          </button>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- Tab Auditoría                                                 -->
      <!-- ============================================================ -->
      <section *ngIf="activeTab === 'auditoria'">
        <!-- Filtros -->
        <div class="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap items-center gap-3">
          <select [(ngModel)]="auditTypeFilter"
                  class="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="all">Todos los tipos</option>
            <option *ngFor="let t of availableAuditTypes" [value]="t">{{ t }}</option>
          </select>
          <select [(ngModel)]="auditUserFilter"
                  class="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="all">Todos los usuarios</option>
            <option *ngFor="let u of availableAuditUsers" [value]="u.id">{{ u.name }}</option>
          </select>
          <input type="date" [(ngModel)]="auditDateFrom"
                 class="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
          <input type="date" [(ngModel)]="auditDateTo"
                 class="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
        </div>

        <!-- Timeline / componente de auditoría existente -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <app-enrollment-audit-timeline
            *ngIf="agreementId"
            [enrollmentId]="agreementId"
            mode="corporate">
          </app-enrollment-audit-timeline>
        </div>
      </section>
    </ng-container>
  </div>
</div>
<!-- Modal de Confirmación de Cambio de Estado -->
<div *ngIf="showStatusConfirmationModal"
  class="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300"
  (click)="onStatusConfirmationCancelled()" (keydown.escape)="onStatusConfirmationCancelled()">
  <div
    class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform transition-all duration-300 scale-100"
    (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500">
      <div class="flex items-center gap-3">
        <div class="p-2 bg-white bg-opacity-20 rounded-lg">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 class="text-lg font-bold text-white">{{ statusConfirmationConfig.title }}</h3>
      </div>
    </div>

    <!-- Body -->
    <div class="px-6 py-5">
      <p class="text-gray-700 text-sm whitespace-pre-line mb-4">{{ statusConfirmationConfig.message }}</p>

      <!-- Input de Razón (condicional) -->
      <div *ngIf="showReasonInput" class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          <span class="text-red-500">*</span> Razón del cambio:
        </label>
        <textarea [(ngModel)]="statusChangeReason" rows="3" placeholder="Ingresa la razón para este cambio de estado..."
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm resize-none transition-colors"></textarea>
        <p *ngIf="showReasonInput && !statusChangeReason.trim()" class="text-xs text-red-500 mt-1">
          Este campo es obligatorio
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
      <button (click)="onStatusConfirmationCancelled()"
        class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
        {{ statusConfirmationConfig.cancelText }}
      </button>
      <button (click)="onStatusConfirmationConfirmed()" [disabled]="showReasonInput && !statusChangeReason.trim()"
        class="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
        {{ statusConfirmationConfig.confirmText }}
      </button>
    </div>
  </div>
</div>

<!-- Modal de Confirmación de Anulación -->
<div *ngIf="showCancellationModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
  role="dialog" aria-modal="true">
  <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"
      (click)="closeCancellationModal()"></div>

    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

    <div
      class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
      <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Anular Inscripción Corporativa</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                ¿Estás seguro de que deseas anular esta inscripción corporativa? Esta acción revertirá el estado,
                desactivará los planes de pago de la empresa y <strong>todos los participantes asociados</strong>.
              </p>
              <div class="mt-4">
                <label for="reason" class="block text-sm font-medium text-gray-700">Motivo de anulación *</label>
                <textarea id="reason" rows="3" [(ngModel)]="cancellationReason"
                  class="mt-1 shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Describe el motivo de la anulación..."></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <button type="button" [disabled]="!cancellationReason || isCancelling" (click)="confirmCancellation()"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isCancelling ? 'Anulando...' : 'Confirmar Anulación' }}
        </button>
        <button type="button" (click)="closeCancellationModal()"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal de Confirmación: Toggle de Cobro Automático -->
<app-confirmation-modal [isVisible]="showAutoPaymentConfirmationModal" [config]="autoPaymentConfirmationConfig"
  (confirmed)="onAutoPaymentConfirmed()" (cancelled)="onAutoPaymentCancelled()">
</app-confirmation-modal>

<!-- 🔐 Modal de operaciones admin sobre cuota corporativa (permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS) -->
<div *ngIf="adminModal.open && adminModal.plan"
     class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-900">
          <ng-container [ngSwitch]="adminModal.mode">
            <span *ngSwitchCase="'menu'">Editar cuota corporativa — Acciones disponibles</span>
            <span *ngSwitchCase="'renumber'">Renumerar cuota</span>
            <span *ngSwitchCase="'date'">Cambiar fecha de vencimiento</span>
            <span *ngSwitchCase="'delete'">Eliminar cuota</span>
            <span *ngSwitchCase="'status'">Cambiar estado</span>
            <span *ngSwitchCase="'attach'">Asociar comprobante de ingreso</span>
            <span *ngSwitchCase="'recalculate'">Recalcular monto pagado</span>
            <span *ngSwitchCase="'punish-remaining'">Castigar saldo restante</span>
          </ng-container>
        </h2>
        <p class="mt-1 text-xs text-gray-500">
          Cuota actual: <span class="font-semibold">#{{ adminModal.plan.installmentNumber }}</span>
          · Vence {{ adminModal.plan.dueDate | date:'dd/MM/yyyy' }}
          · {{ formatCurrencyValue(adminModal.plan.amountToPay) }}
        </p>
      </div>
      <button type="button" (click)="closeAdminModal()" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- 🆕 GRILLA DE ACCIONES (cuando mode='menu') -->
    <div *ngIf="adminModal.mode === 'menu'" class="mt-5">
      <p class="text-sm text-gray-600 mb-3">Elegí qué querés hacer con esta cuota:</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button type="button" (click)="selectAdminMode('recalculate')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-emerald-300 hover:bg-emerald-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Recalcular monto pagado</span>
            <span class="block text-xs text-gray-500">Suma los recibos asociados y actualiza el estado.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('status')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-teal-300 hover:bg-teal-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Cambiar estado</span>
            <span class="block text-xs text-gray-500">Forzar PAGADO / PARCIAL / PENDIENTE / etc.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('attach')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-purple-300 hover:bg-purple-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Asociar comprobante</span>
            <span class="block text-xs text-gray-500">Enganchar un IncomeReceipt existente por ID.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('renumber')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-amber-300 hover:bg-amber-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Renumerar</span>
            <span class="block text-xs text-gray-500">Cambiar el número de cuota (#1, #2…).</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('date')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-sky-300 hover:bg-sky-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Cambiar fecha</span>
            <span class="block text-xs text-gray-500">Modificar la fecha de vencimiento.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('delete')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-rose-300 hover:bg-rose-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Eliminar cuota</span>
            <span class="block text-xs text-gray-500">Soft delete (archivar con audit trail).</span>
          </span>
        </button>

        <!-- Castigar saldo restante: sólo tiene sentido si hay saldo > 0 (parcial). -->
        <button type="button" (click)="selectAdminMode('punish-remaining')"
          *ngIf="adminModal.plan && (adminModal.plan.amountToPay - adminModal.plan.amountPaid) > 0 && !adminModal.plan.isPunished && adminModal.plan.paymentStatus !== 'PAID'"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-orange-300 hover:bg-orange-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Castigar saldo restante</span>
            <span class="block text-xs text-gray-500">Cierra la cuota al monto pagado y crea una nueva castigada por el saldo.</span>
          </span>
        </button>
      </div>
    </div>

    <!-- Botón "← Volver" cuando estamos en un modo específico -->
    <div *ngIf="adminModal.mode && adminModal.mode !== 'menu'" class="mt-3">
      <button type="button" (click)="backToAdminMenu()"
        class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800">
        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        Volver a acciones
      </button>
    </div>

    <!-- 🆕 Recalcular monto pagado -->
    <div *ngIf="adminModal.mode === 'recalculate'" class="mt-4">
      <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p class="font-semibold">¿Qué va a pasar?</p>
        <ul class="mt-2 list-disc pl-5 space-y-1 text-xs">
          <li>Se suman los <strong>recibos activos</strong> asociados a esta cuota corporativa (vía CorporatePaymentPlanId y vía LineageId).</li>
          <li>Se compara con el <strong>monto a pagar</strong> de la cuota.</li>
          <li>Se setea el estado correcto: PENDIENTE / PARCIALMENTE PAGADO / PAGADO.</li>
          <li><strong>No requiere comentario</strong> — es una operación idempotente y segura.</li>
        </ul>
      </div>
    </div>

    <!-- 🆕 Castigar saldo restante -->
    <div *ngIf="adminModal.mode === 'punish-remaining' && adminModal.plan" class="mt-4 space-y-3">
      <div class="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
        <p class="font-semibold">¿Qué va a pasar?</p>
        <ul class="mt-2 list-disc pl-5 space-y-1 text-xs">
          <li>La cuota <strong>#{{ adminModal.plan.installmentNumber }}</strong> se cierra al monto efectivamente pagado
            (<strong>{{ formatCurrencyValue(adminModal.plan.amountPaid) }}</strong>)
            y queda <strong>PAGADA</strong> + bloqueada.</li>
          <li>Se crea una <strong>nueva cuota castigada</strong> por el saldo restante
            (<strong>{{ formatCurrencyValue((adminModal.plan.amountToPay - adminModal.plan.amountPaid)) }}</strong>),
            con la misma fecha de vencimiento ({{ adminModal.plan.dueDate | date:'mediumDate' }}).</li>
          <li>Se registra un <strong>movimiento de cartera corporativo</strong> (ManualPunishment) con motivo, para trazabilidad.</li>
          <li>La nueva cuota va al <strong>final del plan</strong> (no renumera las existentes).</li>
        </ul>
      </div>
      <label class="block text-sm font-semibold text-gray-800">Motivo del castigo</label>
      <textarea rows="3" [(ngModel)]="adminModal.comment"
        placeholder="Ej: Saldo no cobrable, empresa fuera de contacto, etc."
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"></textarea>
      <p class="text-xs text-gray-500">Mínimo 10 caracteres. Queda en el CorporatePortfolioMovement y en el audit trail de ambas cuotas.</p>
    </div>

    <!-- Inputs por modo -->
    <div *ngIf="adminModal.mode === 'renumber'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nuevo número de cuota</label>
      <input type="number" min="1" [(ngModel)]="adminModal.newNumber"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
      <p class="mt-1 text-xs text-gray-500">No puede chocar con otra cuota activa del mismo alcance (empresa/participante + payerType).</p>
    </div>

    <div *ngIf="adminModal.mode === 'date'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nueva fecha de vencimiento</label>
      <input type="date" [(ngModel)]="adminModal.newDate"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
    </div>

    <div *ngIf="adminModal.mode === 'status'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nuevo estado</label>
      <select [(ngModel)]="adminModal.newStatus"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
        <option value="AUTO">🔄 Auto (recalcular desde recibos)</option>
        <option value="PENDING">⏳ Pendiente</option>
        <option value="PAID">✅ Pagado</option>
        <option value="PARTIALLY_PAID">🟡 Parcialmente pagado</option>
        <option value="OVERDUE">🔴 Vencido</option>
        <option value="LINK_GENERATED">🔗 Link generado</option>
      </select>
      <div class="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
        <p class="font-semibold">¿Qué hace "Auto"?</p>
        <p class="mt-1">El backend suma los recibos activos asociados a esta cuota corporativa (vía CorporatePaymentPlanId o LineageId) y determina el estado correcto:</p>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
          <li><strong>Sin recibos</strong> → PENDIENTE</li>
          <li><strong>0 &lt; recibos &lt; monto</strong> → PARCIALMENTE PAGADO</li>
          <li><strong>recibos ≥ monto</strong> → PAGADO (y se lockea)</li>
        </ul>
      </div>
    </div>

    <div *ngIf="adminModal.mode === 'attach'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">ID del comprobante de ingreso</label>
      <input type="number" min="1" [(ngModel)]="adminModal.attachReceiptId"
        placeholder="Ej: 5406"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono" />
      <div class="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
        <p class="font-semibold">¿Qué hace esta acción?</p>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
          <li>Reasocia el <strong>IncomeReceipt #{{ adminModal.attachReceiptId || 'X' }}</strong> a esta cuota corporativa.</li>
          <li>Una cuota puede tener N comprobantes — esta acción <strong>agrega</strong>, no reemplaza los existentes.</li>
          <li>Si el recibo estaba en otra cuota corporativa, se quita de allá y se mueve aquí.</li>
          <li>Si estaba en una cuota individual u otro acuerdo, requiere el checkbox de abajo para confirmar.</li>
          <li>Tras asociar, el status de ambas cuotas (vieja y nueva) se recalcula automáticamente.</li>
        </ul>
      </div>
      <label class="mt-3 flex cursor-pointer items-start gap-2 text-xs text-gray-700">
        <input type="checkbox" [(ngModel)]="adminModal.forceFromOtherEnrollment" class="mt-0.5">
        <span>Forzar reasignación incluso si el comprobante pertenece a OTRO acuerdo o a una cuota individual.</span>
      </label>
    </div>

    <div *ngIf="adminModal.mode === 'delete'" class="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
      <p class="font-semibold">Vas a eliminar (soft delete) esta cuota corporativa.</p>
      <p class="mt-1 text-xs">La cuota queda <span class="font-mono">IsActive=false, IsDeleted=true</span>, archivada con audit trail. Si tiene recibos asociados, te va a pedir confirmación explícita.</p>
      <label class="mt-3 flex cursor-pointer items-start gap-2 text-xs">
        <input type="checkbox" [(ngModel)]="adminModal.forceIfHasReceipts" class="mt-0.5">
        <span>Forzar eliminación incluso si la cuota tiene recibos asociados (los recibos quedarán huérfanos).</span>
      </label>
    </div>

    <!-- Comentario obligatorio (solo para acciones que mutan datos) -->
    <div *ngIf="adminModal.mode && adminModal.mode !== 'menu' && adminModal.mode !== 'recalculate'" class="mt-5">
      <label class="block text-sm font-semibold text-gray-800">Comentario para audit trail (obligatorio)</label>
      <textarea [(ngModel)]="adminModal.comment" rows="3" maxlength="2000"
        placeholder="Ej: Corrección manual previa a refinanciamiento — duplicado del bug de versionado viejo."
        class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"></textarea>
      <p class="mt-1 text-xs text-gray-500">{{ (adminModal.comment || '').length }} / 2000 (mín. 10)</p>
    </div>

    <!-- Footer -->
    <div class="mt-6 flex flex-col gap-3 sm:flex-row">
      <button type="button" (click)="closeAdminModal()"
        class="inline-flex flex-1 items-center justify-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
        {{ adminModal.mode === 'menu' ? 'Cerrar' : 'Cancelar' }}
      </button>
      <button *ngIf="adminModal.mode && adminModal.mode !== 'menu'"
        type="button" (click)="confirmAdminAction()"
        [ngClass]="(adminModal.mode === 'delete' || adminModal.mode === 'punish-remaining')
          ? 'border-rose-700 bg-rose-700 text-white hover:bg-rose-800'
          : 'border-gray-900 bg-gray-900 text-white hover:bg-gray-800'"
        class="inline-flex flex-1 items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold">
        <ng-container [ngSwitch]="adminModal.mode">
          <span *ngSwitchCase="'renumber'">Renumerar</span>
          <span *ngSwitchCase="'date'">Cambiar fecha</span>
          <span *ngSwitchCase="'delete'">Eliminar cuota</span>
          <span *ngSwitchCase="'status'">Cambiar estado</span>
          <span *ngSwitchCase="'attach'">Asociar comprobante</span>
          <span *ngSwitchCase="'recalculate'">Recalcular ahora</span>
          <span *ngSwitchCase="'punish-remaining'">Castigar saldo</span>
        </ng-container>
      </button>
    </div>
  </div>
</div>

<!-- 🆕 Modal de confirmación de anulación de comprobante -->
<div *ngIf="cancelReceiptModal.open" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true"
  aria-labelledby="cancel-receipt-modal-title">
  <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300"
    (click)="closeCancelReceiptModal()"></div>
  <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 relative">
    <div
      class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-lg">
      <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <i class="fas fa-ban text-red-600 text-xl"></i>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
            <h3 class="text-base font-semibold leading-6 text-gray-900" id="cancel-receipt-modal-title">
              Anular Comprobante de Ingreso
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 leading-relaxed">
                Esta acción revierte el pago en la cuota asociada (la deja como
                <strong>Pendiente</strong> o <strong>Parcialmente pagada</strong> según corresponda)
                y registra el evento en el historial. No se puede deshacer.
              </p>
            </div>
            <div *ngIf="cancelReceiptModal.receipt" class="mt-3 bg-gray-50 rounded-lg p-3">
              <div class="text-sm text-gray-700">
                <p><strong>Comprobante:</strong> #{{ cancelReceiptModal.receipt.id }}</p>
                <p *ngIf="cancelReceiptModal.receipt.concept">
                  <strong>Concepto:</strong> {{ cancelReceiptModal.receipt.concept }}
                </p>
                <p><strong>Monto bruto:</strong>
                  {{ formatCurrencyValue(cancelReceiptModal.receipt.grossAmount,
                     cancelReceiptModal.receipt.currencySymbol, cancelReceiptModal.receipt.currencyCode) }}
                </p>
                <p *ngIf="cancelReceiptModal.receipt.transactionDate">
                  <strong>Fecha de pago:</strong>
                  {{ cancelReceiptModal.receipt.transactionDate | date:'mediumDate' }}
                </p>
              </div>
            </div>
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Motivo de anulación <span class="text-red-600">*</span>
              </label>
              <textarea [(ngModel)]="cancelReceiptModal.reason"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                rows="3" placeholder="Especifique el motivo de la anulación (mín. 10 caracteres)..."
                maxlength="1000" [disabled]="cancelReceiptModal.submitting" required></textarea>
              <div class="mt-1 text-xs text-gray-500">
                {{ (cancelReceiptModal.reason.length || 0) }}/1000 caracteres
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <button type="button" [disabled]="cancelReceiptModal.submitting"
          class="w-full inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          (click)="confirmCancelReceipt()">
          <i class="fas fa-ban mr-2"></i>
          {{ cancelReceiptModal.submitting ? 'Anulando...' : 'Confirmar Anulación' }}
        </button>
        <button type="button" [disabled]="cancelReceiptModal.submitting"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-colors duration-200 disabled:opacity-50"
          (click)="closeCancelReceiptModal()">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>
<!-- ============================================================ -->
<!-- 🆕 Modal editar valor de matrícula por estudiante (ADMIN_OPS) -->
<!-- ============================================================ -->
<div *ngIf="editAmountModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <h2 class="text-xl font-bold text-gray-900">Editar valor de matrícula por estudiante</h2>
      <button type="button" (click)="closeEditAmountModal()" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div *ngIf="editAmountModal.loading" class="mt-6 text-sm text-gray-500">Cargando referencia del proyecto…</div>

    <ng-container *ngIf="!editAmountModal.loading && editAmountModal.context">
      <!-- Paso 1: edición -->
      <div *ngIf="!editAmountModal.confirmStep" class="mt-4 space-y-3 text-sm">
        <div class="rounded-lg bg-gray-50 p-3 space-y-1">
          <div class="flex justify-between"><span class="text-gray-500">Valor de referencia del proyecto</span>
            <span class="font-semibold">{{ formatCurrencyValue(editAmountModal.context.referenceAmount, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Valor actual por estudiante</span>
            <span class="font-semibold">{{ editAmountModal.context.perParticipantAmount != null
              ? formatCurrencyValue(editAmountModal.context.perParticipantAmount, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode())
              : 'Sin definir (usa referencia)' }}</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Descuento actual</span>
            <span class="font-semibold">{{ editAmountModal.context.currentDiscount | number:'1.0-2' }}%</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Descuento máximo permitido</span>
            <span class="font-semibold">{{ editAmountModal.context.maxPercentDiscount }}%</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Participantes activos</span>
            <span class="font-semibold">{{ editAmountModal.context.activeParticipantsCount }}</span></div>
        </div>

        <label class="block">
          <span class="font-medium text-gray-700">Nuevo valor por estudiante</span>
          <input type="text" inputmode="numeric" appCurrencyFormatter [(ngModel)]="editAmountModal.newValue"
                 class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
        </label>

        <div class="rounded-lg bg-emerald-50 p-3 text-emerald-800">
          Nuevo descuento: <span class="font-bold">{{ editAmountPreview.discount | number:'1.0-2' }}%</span>
          · −{{ formatCurrencyValue(editAmountPreview.discountAmount, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}
          <div class="mt-1 text-xs">
            Total acuerdo estimado: <b>{{ formatCurrencyValue(editAmountEstimatedTotal, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}</b>
            ({{ editAmountModal.context.activeParticipantsCount }} participante(s))
          </div>
          <div *ngIf="editAmountPreview.aboveReference" class="mt-1 text-xs">El valor supera la referencia: queda sin descuento (0%).</div>
        </div>

        <label class="block">
          <span class="font-medium text-gray-700">Comentario (obligatorio, mín. 10 caracteres)</span>
          <textarea rows="2" [(ngModel)]="editAmountModal.comment"
                    class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"></textarea>
        </label>

        <div class="flex justify-end gap-2 pt-2">
          <button type="button" (click)="closeEditAmountModal()" class="rounded-lg border border-gray-300 px-4 py-2">Cancelar</button>
          <button type="button" (click)="confirmEditAmount()"
                  class="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white disabled:opacity-40">Continuar</button>
        </div>
      </div>

      <!-- Paso 2: confirmación -->
      <div *ngIf="editAmountModal.confirmStep" class="mt-4 space-y-4 text-sm">
        <div class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <div class="font-semibold">¿Confirmas el cambio?</div>
          <div class="mt-1">
            El valor por estudiante pasará a
            <b>{{ formatCurrencyValue(editAmountModal.newValue ?? 0, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}</b>
            con descuento de <b>{{ editAmountPreview.discount | number:'1.0-2' }}%</b>
            (total acuerdo estimado:
            <b>{{ formatCurrencyValue(editAmountEstimatedTotal, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}</b>).
            Después deberás <b>editar el plan de pagos</b> para redistribuir los saldos de las cuotas.
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" (click)="editAmountModal.confirmStep = false" [disabled]="editAmountModal.saving"
                  class="rounded-lg border border-gray-300 px-4 py-2">Volver</button>
          <button type="button" (click)="saveEditAmount()" [disabled]="editAmountModal.saving"
                  class="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white disabled:opacity-40">
            {{ editAmountModal.saving ? 'Guardando…' : 'Sí, guardar' }}
          </button>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<!-- ============================================================ -->
<!-- 🆕 Modal papelera de cuotas corporativas (ENROLLMENTS_PAYMENT_PLAN_TRASH) -->
<!-- ============================================================ -->
<div *ngIf="trashModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-900">Papelera de cuotas</h2>
        <p class="mt-1 text-xs text-gray-500">Cuotas eliminadas del acuerdo. Restaurar las reasigna a la versión activa de su scope.</p>
      </div>
      <button type="button" (click)="trashModal.open = false" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Tabs empresa / participantes -->
    <div class="mt-3 flex gap-1 rounded-lg bg-gray-100 p-1 text-xs font-semibold">
      <button type="button" (click)="trashTab = 'company'"
              [ngClass]="trashTab === 'company' ? 'bg-white shadow text-gray-900' : 'text-gray-500'"
              class="flex-1 rounded-md px-3 py-1.5">Empresa ({{ trashCompanyItems.length }})</button>
      <button type="button" (click)="trashTab = 'participants'"
              [ngClass]="trashTab === 'participants' ? 'bg-white shadow text-gray-900' : 'text-gray-500'"
              class="flex-1 rounded-md px-3 py-1.5">Participantes ({{ trashParticipantItems.length }})</button>
    </div>

    <div *ngIf="(trashTab === 'company' ? trashCompanyItems : trashParticipantItems).length === 0"
         class="mt-6 text-sm text-gray-500">No hay cuotas eliminadas en este scope.</div>

    <div *ngIf="(trashTab === 'company' ? trashCompanyItems : trashParticipantItems).length > 0"
         class="mt-4 max-h-96 overflow-y-auto">
      <table class="w-full text-sm">
        <thead class="text-left text-xs text-gray-500">
          <tr>
            <th class="py-1.5 pr-2">#</th>
            <th *ngIf="trashTab === 'participants'" class="py-1.5 pr-2">Participante</th>
            <th class="py-1.5 pr-2">Monto</th>
            <th class="py-1.5 pr-2">Vencimiento</th>
            <th class="py-1.5 pr-2">Eliminada</th>
            <th class="py-1.5 pr-2">Por</th>
            <th class="py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of (trashTab === 'company' ? trashCompanyItems : trashParticipantItems)" class="border-t border-gray-100">
            <td class="py-2 pr-2 font-semibold">#{{ item.installmentNumber }}</td>
            <td *ngIf="trashTab === 'participants'" class="py-2 pr-2 text-xs">{{ getTrashParticipantName(item) }}</td>
            <td class="py-2 pr-2 tabular-nums">{{ formatCurrencyValue(item.amountToPay, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }}</td>
            <td class="py-2 pr-2">{{ item.dueDate | date:'dd/MM/yyyy' }}</td>
            <td class="py-2 pr-2">{{ item.deletedAt ? (item.deletedAt | date:'dd/MM/yyyy HH:mm') : '—' }}</td>
            <td class="py-2 pr-2 text-xs">{{ item.deletedBy ?? '—' }}</td>
            <td class="py-2 text-right">
              <button type="button" (click)="askRestore(item)"
                      class="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                Restaurar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirmación de restauración -->
    <div *ngIf="trashModal.restoring" class="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
      <div class="font-semibold text-emerald-800">
        Restaurar cuota #{{ trashModal.restoring.installmentNumber }}
        ({{ formatCurrencyValue(trashModal.restoring.amountToPay, getEnrollmentCurrencySymbol(), getEnrollmentCurrencyCode()) }})
        <span *ngIf="trashModal.restoring.participantId != null"> — {{ getTrashParticipantName(trashModal.restoring) }}</span>
      </div>
      <p class="mt-1 text-xs text-emerald-700">Volverá activa en la versión actual de su scope y se recalculará su estado de pago.</p>
      <textarea rows="2" [(ngModel)]="trashModal.comment" placeholder="Comentario obligatorio (mín. 10 caracteres)"
                class="mt-2 w-full rounded-lg border border-emerald-300 px-3 py-2"></textarea>
      <div class="mt-2 flex justify-end gap-2">
        <button type="button" (click)="trashModal.restoring = null" [disabled]="trashModal.saving"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs">Cancelar</button>
        <button type="button" (click)="confirmRestore()" [disabled]="trashModal.saving"
                class="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
          {{ trashModal.saving ? 'Restaurando…' : 'Confirmar restauración' }}
        </button>
      </div>
    </div>
  </div>
</div>
